// 📦 React & Routing
import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';

// 🧩 Redux
import { connect } from 'react-redux';

// 🔣 Libraries & Utilities
import Decimal from 'decimal.js';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

// 🧠 Helpers & Constants
import Helpers from '@Apis/helpers';
import FormHelpers from '@Helpers/FormHelpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import Constants, {
  QUANTITY,
  ISTDateFormat,
  DEFAULT_CUR_ROUND_OFF,
} from '@Apis/constants';

// 🧾 Redux Actions
import DNActions from '@Actions/dnActions.js';
import OfferActions from '@Actions/offerActions';
import SellerActions from '@Actions/sellerActions';
import GrnActions from '@Actions/grnActions';
import CurrenciesActions from '@Actions/configurations/currenciesAction';
import ExtraChargesActions from '@Actions/configurations/extraChargesAction';
import CFV2Actions from '@Actions/configurations/cfV2Actions';
import TaxActions from '@Actions/taxActions';

// 🎨 UI Components - H3 UI Kit
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import H3Text from '@Uilib/h3Text';

// 🎛️ Common UI & Forms
import AddressSelector from '@Components/Common/FormUtils/AddressSelecter';
import SelectDepartment from '@Components/Common/SelectDepartment';
import SelectSeller from '../../../Common/SelectSeller';
import RichTextEditor from '../../../Common/RichTextEditor';
import SelectTaxType from '../../../Admin/Common/SelectTaxType';
import SelectExtraCharge from '../../../Admin/Common/SelectExtraCharge';
import CurrencyConversionV2 from '../../../Common/CurrencyConversionV2';
import CustomFieldV3 from '@Components/Common/CustomFieldV3';
import FreightTaxInput from '@Components/Common/FreightTaxInput';
import ChargesTaxInput from '@Components/Common/ChargesTaxInput';
import PRZSelect from '../../../Common/UI/PRZSelect';
import DocumentNumberSeqInput from '../../../Admin/Common/DocumentNumberSeqInput';

// 🔒 Restricted Components
import HideValue from '../../../Common/RestrictedAccess/HideValue';
import RestrictedAccessMessage from '../../../Common/RestrictedAccess/RestrictedAccessMessage';

// ⚙️ Features & Business Logic
import DNLines from './DNLines';
import dnErrorList from './DnErrors';
import ErrorHandle from '../../../Common/ErrorHandle';

// 🧰 Ant Design Components
import {
  DatePicker,
  Drawer,
  Select,
  Upload,
  Checkbox,
  Popconfirm,
  Tooltip,
} from 'antd';

// 🎨 Icons
import {
  EditFilled,
  PlusOutlined,
  PlusCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleXmark,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';

// 🖼️ Static Assets
import Crown from '@Images/crown2.png';

// 🖌️ Styles
import './style.scss';


const { Option } = Select;
const uploadButton = (
  <div>
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>Upload</div>
  </div>
);

class DebitNoteForm extends Component {

  constructor(props) {
    super(props);
    this.state = {
      data: [],
      chargeData: [],
      dnDate: dayjs(),
      checkedRecipients: false,
      toRecipients: [],
      allStock: false,
      taxTypeName: 'TCS',
      selectedCurrencyName: '',
      selectedCurrencyID: '',
      tenantDepartmentId: null,
      charge1Name: 'Freight',
      charge1Value: '',
      discountType: 'Percent',
      isAutomaticConversionRate: null,
      currencyConversionRate: '',
      debitNoteWithReturnedQyt: false,
      debitNoteWithoutLines: false,
      selectedSellerCurrencyInfo: null,
      narration: '',
      selectedTenantTallyIntegrationId: props?.user?.tenant_info?.it_id,
      dnNumber: '',
    };
  }

  componentWillUnmount() {
    const {
      getDNByIdSuccess, getDocCFV2Success,
    } = this.props;
    getDocCFV2Success(null);
    getDNByIdSuccess(null);
  }

  componentDidMount() {
    const {
      getDNById, user, match, getSellers, selectedGrnForDN, getDocCFV2, getCurrencies, getCharges, dnFormFromAPInvoice, getTaxes,
    } = this.props;

    const { allStock } = this.state;
    getCurrencies();
    const payload = {
      orgId: user?.tenant_info?.org_id,
      entityName: 'DEBIT_NOTE',
    };
    getDocCFV2(payload);
    const { debitNoteId } = match.params;
    if (debitNoteId) {
      getDNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.DEBIT_NOTE, Helpers.permissionTypes.READ).join(','), debitNoteId, (selectedDNData) => {
        const tenantId = selectedDNData?.data?.debit_notes?.[0]?.tenant_id;
        getSellers('', tenantId, 1, 30);
      });
    } else {
      this.setState({
        billingAddress: user?.tenant_info?.default_billing_address_info,
        tenantDepartmentId: user?.tenant_info?.default_store_id,
      });
      getSellers('', selectedGrnForDN?.tenant_info?.tenant_id || user?.tenant_info?.tenant_id, 1, 30, selectedGrnForDN?.seller_id);
    }

    if (selectedGrnForDN) {
      const lines = dnFormFromAPInvoice ? selectedGrnForDN?.ap_invoice_lines : selectedGrnForDN?.grn_lines;
      const grnLines = lines?.map((orderLine) => ({
        key: uuidv4(),
        asset1: orderLine?.product_sku_info?.assets?.[0]?.url || '',
        product_sku_name: orderLine?.product_sku_info?.product_sku_name,
        product_sku_info: orderLine?.product_sku_info,
        offerPrice: Number(orderLine?.offer_price),
        lineDiscountType: orderLine?.is_discount_in_percent ? 'Percent' : 'Amount',
        discount: orderLine?.is_discount_in_percent ? orderLine?.line_discount_percentage : orderLine?.line_discount_amount,
        order_line_id: orderLine?.order_line_id,
        tenant_product_id: orderLine?.tenant_product_id,
        manufacturingDateFormat: orderLine?.product_sku_info?.manufacturing_date_format,
        expiryDateFormat: orderLine?.product_sku_info?.expiry_date_format,
        quantity: QUANTITY(orderLine?.received_qty - orderLine?.dn_qty, orderLine?.uom_info?.[0]?.precision),
        unitPrice: Number(orderLine?.offer_price),
        grnRecivedQty: Number(orderLine?.received_qty),
        existingDnQuantity: Number(orderLine?.dn_qty),
        taxId: Number(orderLine?.tax_id),
        uomId: orderLine?.product_sku_info?.uom_id ? orderLine?.product_sku_info?.uom_id : '',
        uom_info: orderLine?.uom_info?.[0],
        taxInfo: orderLine?.tax_group_info,
        productCategoryInfo: orderLine?.product_sku_info?.product_category_info || {},
        child_taxes: Helpers.computeTaxation(((orderLine?.received_qty - orderLine?.dn_qty) * orderLine.offer_price) * (orderLine.line_discount_percentage ? Number(100 - orderLine.line_discount_percentage) / 100 : 1), orderLine?.tax_group_info, selectedGrnForDN?.tenant_info?.default_billing_address_info?.state, selectedGrnForDN?.seller_address_info?.state)?.tax_info?.child_taxes,
        grnLineId: orderLine?.grn_line_id,
        apInvoiceLineId: orderLine?.ap_invoice_line_id,
        batchId: orderLine?.batch_id,
        product_batches: this.getBatches(Helpers.batchSelectionMethod(orderLine?.default_outwards_method || Helpers.getBatchMethod(orderLine), orderLine?.product_batches, orderLine?.quantity), orderLine?.uom_info?.[0]?.ratio, orderLine?.product_sku_info?.ratio, QUANTITY(orderLine?.received_qty - orderLine?.dn_qty, orderLine?.uom_info?.[0]?.precision)),
        updateStock: true,
        remarks: orderLine?.remarks?.replace(/<[^>]+>/g, ''),
        batchConsumptionMethod: Helpers.getBatchMethod(orderLine),
        returned_quantity: QUANTITY(orderLine?.received_qty - orderLine?.dn_qty, orderLine?.uom_info?.[0]?.precision),
      }));
      // getGRN(user?.tenant_info?.tenant_id, '', '', '', '', '', '', selectedGrnForDN?.tenant_seller_info?.tenant_seller_id);
      const email = [selectedGrnForDN?.email_id_1 || '', selectedGrnForDN?.created_by_info?.email || ''];
      const recipients = [];
      for (let i = 0; i < email?.length; i++) {
        if (Helpers.validateEmail(email[i])) {
          recipients.push(email[i]);
        }
      }
      const grnChargeData = selectedGrnForDN?.other_charges?.map((item) => ({
        ...item,
        chargeKey: uuidv4(),
        chargesTaxId: item?.tax_info?.tax_id,
        chargesSacCode: item?.charge_sac_code,
        chargesTaxInfo: item?.tax_info,
        chargesTax: item?.tax_info?.tax_value,
        tallyLedgerName: item?.ledger_name || null,
        chargesTaxData: {
          ...item?.tax_info,
          child_taxes: Helpers.computeTaxation(item?.charge_amount, item?.tax_info, selectedGrnForDN?.tenant_info?.default_billing_address_info?.state, selectedGrnForDN?.seller_address_info?.state)?.tax_info?.child_taxes,
        },
      })) || [];
      this.setState({
        selectedSeller: selectedGrnForDN?.tenant_seller_info?.tenant_seller_id || selectedGrnForDN?.tenant_seller_id,
        selectedSelerInfo: selectedGrnForDN?.tenant_seller_info,
        billingAddress: user?.tenant_info?.default_billing_address_info,
        data: grnLines || [],
        selectedGrn: selectedGrnForDN?.grn_id,
        fileList: selectedGrnForDN?.attachments,
        gstNumber: selectedGrnForDN?.tenant_seller_info?.gst_number,
        shippingAddress: selectedGrnForDN?.seller_address_info,
        toRecipients: recipients,
        discountPercentage: selectedGrnForDN?.is_discount_in_percent ? selectedGrnForDN?.discount_percentage?.toFixed(DEFAULT_CUR_ROUND_OFF) || 0 : selectedGrnForDN?.discount_amount?.toFixed(DEFAULT_CUR_ROUND_OFF) || 0,
        isLineWiseDiscount: !selectedGrnForDN?.is_line_wise_discount,
        chargeData: grnChargeData,
        taxType: selectedGrnForDN?.tcs_id ? selectedGrnForDN?.tcs_id : null,
        taxTypeId: selectedGrnForDN?.tcs_id ? selectedGrnForDN?.tcs_info?.tax_id : null,
        taxTypeInfo: selectedGrnForDN?.tcs_id ? selectedGrnForDN?.tcs_info : null,
        selectedCurrencyID: selectedGrnForDN?.org_currency_info?.org_currency_id,
        selectedCurrencyName: selectedGrnForDN?.org_currency_info,
        taxTypeName: selectedGrnForDN?.tcs_id ? selectedGrnForDN?.tcs_info?.tax_type_name : 'TCS',
        tenantDepartmentId: selectedGrnForDN?.tenant_department_id,
        charge1Name: selectedGrnForDN?.charge_1_name || 'Freight',
        charge1Value: selectedGrnForDN?.charge_1_value,
        freightTaxId: selectedGrnForDN?.freight_tax_id || 'Not Applicable',
        freightTax: selectedGrnForDN?.freight_tax_info?.tax_value,
        freightTaxInfo: selectedGrnForDN?.freight_tax_info,
        freightTaxData: {
          ...selectedGrnForDN?.freight_tax_info,
          child_taxes: Helpers.computeTaxation(selectedGrnForDN?.charge_1_value, selectedGrnForDN?.freight_tax_info, selectedGrnForDN?.tenant_info?.default_billing_address_info?.state, selectedGrnForDN?.seller_address_info?.state)?.tax_info?.child_taxes,
        },
        freightSacCode: selectedGrnForDN?.freight_sac_code,
        discountType: selectedGrnForDN?.is_discount_in_percent ? 'Percent' : 'Amount',
        isAutomaticConversionRate: false,
        currencyConversionRate: selectedGrnForDN?.conversion_rate,
        narration: '',
        selectedTenantTallyIntegrationId: user?.user_tenants?.find((item) => item?.tenant_id === selectedGrnForDN?.grn_tenant_id)?.it_id,
      });
    }

    getCharges(user?.tenant_info?.org_id, 'PURCHASE', (charges) => {
      const freight = charges?.find((i) => (i?.ledger_name === 'Freight Charge' && i?.is_system_charge));
      if (freight) {
        this.setState({ freightSacCode: freight?.charge_sac_code });
      }
    });

    getTaxes(user?.tenant_info?.org_id, 1, 1000, null, true);
  }

  getDnErrors() {
    const {
      selectedSeller, tenantDepartmentId, dnDate, cfDebitNoteDoc, checkedRecipients, toRecipients, data, debitNoteWithReturnedQyt, debitNoteWithoutLines, selectedGrn, purchaseAccount, dnNumber,
    } = this.state;
    const { user } = this.props;
    const { docLevelError, lineLevelError } = dnErrorList({
      selectedSeller, tenantDepartmentId, dnDate, cfDebitNoteDoc, checkedRecipients, toRecipients, data, debitNoteWithReturnedQyt, debitNoteWithoutLines, selectedGrn, purchaseAccount, user, dnNumber,
    });
    return { docLevelError: docLevelError ?? [], lineLevelError: lineLevelError ?? [] };
  }

  static getDerivedStateFromProps(props, state) {
    const { user, CurrenciesResults } = props;

    if (CurrenciesResults && CurrenciesResults?.length > 0) {
      const defaultCurrency = CurrenciesResults?.find((currency) => currency.is_default === true);
      if (defaultCurrency && !state.selectedCurrencyID) {
        return {
          ...state,
          selectedCurrencyID: defaultCurrency?.org_currency_id,
          selectedCurrencyName: defaultCurrency,
          currencyConversionRate: user?.tenant_info?.global_config?.settings?.automatic_conversion_rate ? defaultCurrency?.automatic_conversion_rate : defaultCurrency?.conversion_rate,
          isAutomaticConversionRate: user?.tenant_info?.global_config?.settings?.automatic_conversion_rate,
        };
      }
    }

    if (props.cfV2DocDebitNote && !state.isUserReadyOne) {
      return {
        ...state,
        cfDebitNoteDoc: CustomFieldHelpers.getCfStructure(props.cfV2DocDebitNote?.data?.document_custom_fields, true) || [],
        isUserReadyOne: true,
      };
    }
    if (props.selectedDN?.data?.debit_notes?.length && props.cfV2DocDebitNote?.data?.success && props?.match?.params?.debitNoteId && !state.isUserReady) {
      const selectedDN = props.selectedDN?.data?.debit_notes[0];

      // Already Used Custom  Fields
      const oldCustomField = selectedDN?.custom_fields || [];

      const tempSellerId = selectedDN?.tenant_seller_id;
      const dnLines = selectedDN?.dn_lines?.map((grnLine) => ({
        key: uuidv4(),
        asset1: grnLine?.product_sku_info?.assets?.[0]?.url || '',
        product_sku_name: grnLine?.product_sku_info?.product_sku_name || grnLine?.product_sku_name,
        productCategoryInfo: grnLine?.product_sku_info?.product_category_info || {},
        unitPrice: Number(grnLine?.unit_price),
        uomId: Number(grnLine?.uom_id),
        taxId: Number(grnLine?.tax_id),
        taxInfo: grnLine?.tax_group_info,
        child_taxes: Helpers.computeTaxation((grnLine.quantity * grnLine.offer_price) * (grnLine.line_discount_percentage ? Number(100 - grnLine.line_discount_percentage) / 100 : 1), grnLine?.tax_group_info, selectedDN?.tenant_info?.default_billing_address_info?.state, selectedDN?.default_shipping_address_info?.state)?.tax_info?.child_taxes,
        lineDiscountType: grnLine?.is_discount_in_percent ? 'Percent' : 'Amount',
        discount: grnLine?.is_discount_in_percent ? grnLine?.line_discount_percentage : grnLine?.line_discount_amount,
        order_line_id: grnLine?.order_line_id,
        tenant_product_id: grnLine?.tenant_product_id,
        uom_info: grnLine?.uom_info[0],
        existingDnQuantity: Number(grnLine?.other_dn_qty),
        grnRecivedQty: grnLine?.received_qty,
        remarks: grnLine?.remarks?.replace(/<[^>]+>/g, '') || '',
        quantity: Number(grnLine?.quantity),
        dnLineId: grnLine?.dn_line_id,
        batch_id: grnLine?.product_batches?.[0]?.batch_id,
        product_batches: grnLine?.product_batches?.map((item) => ({ ...item, returned_quantity: item?.quantity })),
        // product_batches: getBatches(Helpers.batchSelectionMethod(grnLine?.default_outwards_method || Helpers.getBatchMethod(grnLine), grnLine?.product_batches, selectedDN?.bypass_inventory_change ? grnLine?.returned_qty : grnLine?.quantity), grnLine?.uom_info?.[0]?.ratio, grnLine?.product_sku_info?.ratio),
        product_sku_info: grnLine?.product_sku_info,
        updateStock: grnLine?.update_stock_qty,
        batchConsumptionMethod: grnLine?.default_outwards_method || Helpers.getBatchMethod(grnLine),
        returned_quantity: grnLine?.returned_qty,
      }));

      return {
        ...state,
        isUserReady: true,
        selectedSeller: selectedDN?.tenant_seller_id,
        selectedSelerInfo: selectedDN?.tenant_seller_info,
        gstNumber: selectedDN?.tenant_seller_info?.gst_number,
        dnDate: dayjs(selectedDN?.order_date),
        billingAddress: selectedDN?.billing_address_info,
        shippingAddress: selectedDN?.shipping_address_info,
        data: dnLines,
        termsAndConditions: selectedDN?.terms_and_conditions,
        dnNumber: selectedDN?.debit_note_number,
        selectedGrn: selectedDN?.grn_id,
        fileList: selectedDN?.attachments,
        selectedDN,
        cfDebitNoteDoc: CustomFieldHelpers.mergeCustomFields(props.cfV2DocDebitNote?.data?.document_custom_fields, oldCustomField),
        toRecipients: selectedDN?.notification_recipients || [],
        checkedRecipients: selectedDN?.is_automatic_notification_enabled,
        discountPercentage: selectedDN?.is_discount_in_percent ? selectedDN?.discount_percentage : selectedDN?.discount_amount,
        isLineWiseDiscount: !selectedDN?.is_line_wise_discount,
        chargeData: selectedDN?.dn_other_charges?.map((item) => ({
          ...item,
          chargeKey: uuidv4(),
          chargesTaxId: item?.tax_info?.tax_id,
          chargesSacCode: item?.charge_sac_code,
          chargesTaxInfo: item?.tax_info,
          chargesTax: item?.tax_info?.tax_value,
          tallyLedgerName: item?.ledger_name || null,
          chargesTaxData: {
            ...item?.tax_info,
            child_taxes: Helpers.computeTaxation(item?.charge_amount, item?.tax_info, selectedDN?.tenant_info?.default_billing_address_info?.state, selectedDN?.default_shipping_address_info?.state)?.tax_info?.child_taxes,
          },
        })) || [],
        taxType: selectedDN?.tcs_id,
        taxTypeId: selectedDN?.tcs_info?.tax_id,
        taxTypeInfo: selectedDN?.tcs_info,
        selectedCurrencyID: selectedDN?.org_currency_info?.org_currency_id,
        selectedCurrencyName: selectedDN?.org_currency_info,
        taxTypeName: selectedDN?.tcs_info?.tax_type_name,
        tenantDepartmentId: selectedDN?.tenant_department_id,
        charge1Name: selectedDN?.charge_1_name || 'Freight',
        charge1Value: selectedDN?.charge_1_value,
        freightTaxId: selectedDN?.freight_tax_id || 'Not Applicable',
        freightTax: selectedDN?.freight_tax_info?.tax_value,
        freightTaxInfo: selectedDN?.freight_tax_info,
        freightTaxData: {
          ...selectedDN?.freight_tax_info,
          child_taxes: Helpers.computeTaxation(selectedDN?.charge_1_value, selectedDN?.freight_tax_info, selectedDN?.tenant_info?.default_billing_address_info?.state, selectedDN?.default_shipping_address_info?.state)?.tax_info?.child_taxes,
        },
        freightSacCode: selectedDN?.freight_sac_code,
        discountType: selectedDN?.is_discount_in_percent ? 'Percent' : 'Amount',
        debitNoteWithoutLines: selectedDN?.without_lines,
        debitNoteWithReturnedQyt: selectedDN?.bypass_inventory_change,
        isAdhocDnLines: !selectedDN?.grn_id,
        currencyConversionRate: selectedDN?.conversion_rate,
        isAutomaticConversionRate: false,
        purchaseAccount: selectedDN?.tally_purchase_return_account,
        narration: selectedDN?.narration || '',
        selectedTenantTallyIntegrationId: user?.user_tenants?.find((item) => item?.tenant_id === selectedDN?.tenant_id)?.it_id,
      };
    }
    if (props?.selectedGrnForDN && props.cfV2DocDebitNote?.data?.success && !state?.isUserReadyTwo) {

      return {
        ...state,
        isUserReadyTwo: true,
        cfDebitNoteDoc: CustomFieldHelpers.mergeCustomFields(props?.cfV2DocDebitNote?.data?.document_custom_fields || [], props?.selectedGrnForDN?.custom_fields || []) || [],
      };
    }

    return state;
  }

  getBatches = (
    batches,
    uomInfoRatio,
    productSkuInfoRatio,
    totalRemainingQuantity,
    isAdhocDnLines,
    quantity
  ) => {
    const newBatches = [];

    if (isAdhocDnLines) {
      const tempBatches = batches?.map(batch => ({ ...batch }));
      let remainingQty = new Decimal(quantity);

      for (let i = 0; i < tempBatches?.length; i++) {
        tempBatches[i].batch_in_use = true;
      }

      for (let i = 0; i < tempBatches?.length; i++) {
        const avlQty = new Decimal(QUANTITY(tempBatches?.[i]?.available_qty, uomInfoRatio?.precision) || 0).toDecimalPlaces(uomInfoRatio?.precision || 2);
        const currQty = new Decimal(tempBatches[i]?.quantity || 0);

        if (tempBatches[i]?.batch_in_use) {
          if (remainingQty.gt(0) && currQty.lt(avlQty)) {
            const qtyToUse = Decimal.min(avlQty, remainingQty);
            tempBatches[i].quantity = qtyToUse.toNumber();
            tempBatches[i].batch_in_use = true;
            remainingQty = remainingQty.minus(qtyToUse);
          } else {
            tempBatches[i].quantity = 0;
            tempBatches[i].batch_in_use = true;
            remainingQty = remainingQty.minus(tempBatches[i].quantity);
          }
        }
      }

      return tempBatches;
    }

    for (let i = 0; i < batches?.length; i++) {
      const batch = batches[i];
      const consumedQty = new Decimal(batch?.quantity || 0);
      const dnGenQty = new Decimal(batch?.dn_gen_qty || 0);

      newBatches.push({
        ...batch,
        batch_in_use: true,
        consumed_qty: consumedQty.toNumber(),
        quantity: consumedQty.minus(dnGenQty).toNumber(),
      });
    }

    if (totalRemainingQuantity) {
      let remainingQty = new Decimal(totalRemainingQuantity);

      for (let i = 0; i < newBatches?.length; i++) {
        newBatches[i].quantity = 0;
      }

      for (let i = 0; i < newBatches?.length; i++) {
        const consumedQty = new Decimal(newBatches[i]?.consumed_qty || 0);
        const dnGenQty = new Decimal(newBatches[i]?.dn_gen_qty || 0);
        const maxAvailable = Decimal.max(consumedQty.minus(dnGenQty), 0);
        const currQty = new Decimal(newBatches[i].quantity || 0);

        if (remainingQty.gt(0) && currQty.lt(maxAvailable)) {
          const qtyToUse = Decimal.min(maxAvailable, remainingQty);
          newBatches[i].quantity = qtyToUse.toNumber();
          remainingQty = remainingQty.minus(qtyToUse);
        } else {
          newBatches[i].quantity = 0;
          remainingQty = remainingQty.minus(newBatches[i].quantity);
        }
      }
    }
    return newBatches;
  };


  handleChangeTextArea = (html) => {
    this.setState({ termsAndConditions: html });
  }

  handleChangeNarration = (value) => {
    this.setState({ narration: value });
  }

  handleDelete = (key) => {
    const { data } = this.state;
    if (data.length === 1) {
      this.setState({
        data: [{
          key: uuidv4(),
          asset1: '',
          product_sku_name: '',
          quantity: '',
          offerPrice: '',
          unitPrice: '',
          lot: 0,
          taxId: '',
          discount: 0,
          child_taxes: [{
            tax_amount: 0,
            tax_type_name: '',
          }],
        }],
      });
    } else {
      const copyData = data.filter((item) => item.key !== key);
      this.setState({ data: copyData }, () => {
        if (this.state?.isLineWiseDiscount) this.handleDiscountPercentageChange(this.state.discountPercentage);
      });
    }
  }

  handleDiscountPercentageChange = (value) => {
    const { discountType, data, billingAddress, shippingAddress } = this.state;
    const copyData = JSON.parse(JSON.stringify(data));
    copyData.map((item) => {
      const totalValue = copyData?.reduce((acc, cur) => acc + (cur.quantity * cur.unitPrice), 0);

      const discountValue = discountType === 'Percent' ? parseFloat(value) : ((item.quantity * item.unitPrice) / parseFloat(totalValue)) * parseFloat(value);

      const taxableValue = discountType === 'Percent'
        ? (item?.quantity * item?.unitPrice) * (1 - discountValue / 100)
        : Math.max(item.quantity * item?.unitPrice - discountValue, 0);

      item.discount = discountValue;
      item.child_taxes = Helpers.computeTaxation(taxableValue, item?.taxInfo, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes;
      return item;
    });
    this.setState({ data: copyData, discountPercentage: parseFloat(value) });
  }

  handleDeleteCharge = (chargeKey) => {
    const { chargeData } = this.state;
    const copyChargeData = chargeData.filter((item) => item.chargeKey !== chargeKey);
    this.setState({ chargeData: copyChargeData });
  }

  getLineTotals() {
    const {
      data, chargeData, taxTypeInfo, taxTypeName, charge1Value, freightTax, freightTaxData,
    } = this.state;
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalBase = 0;
    let totalTcs = 0;
    let totalTax = 0;
    let totalOtherCharge = 0;
    let dnTotal = 0;
    let totalOtherChargesForTaxableAmount = 0;
    let freightCharge = Number(charge1Value);
    let freightTaxAmount = 0;

    // Taxes Bifurcation
    const totalTaxValue = Helpers.groupAndSumByTaxName(data?.map((item) => item?.child_taxes)?.flat())?.reduce((acc, curr) => acc + curr?.tax_amount, 0);

    for (let i = 0; i < chargeData?.length; i++) {
      let currentOtherCharge = 0;
      let chargesTaxAmountLinesWise = 0;
      if (chargeData[i]?.charge_amount) {
        chargesTaxAmountLinesWise = Helpers.groupAndSumByTaxName([...chargeData[i]?.chargesTaxData?.child_taxes?.flat()])?.reduce((acc, curr) => acc + curr?.tax_amount, 0);
        currentOtherCharge = chargeData?.[i]?.chargesTax ? chargeData?.[i]?.charge_amount + (chargesTaxAmountLinesWise) : chargeData?.[i]?.charge_amount;
        if (chargeData?.[i]?.chargesTax) {
          totalOtherChargesForTaxableAmount += chargeData?.[i]?.charge_amount;
        }
      }
      totalOtherCharge += currentOtherCharge;
    }

    for (let i = 0; i < data.length; i++) {
      let currentAmount = 0;
      let currentDiscount = 0;
      let currentBase = 0;
      let currentTax = 0;
      let currentDN = 0;

      const discountValue = Number(data[i].discount);
      if (data[i].quantity) {
        currentAmount += (data[i].quantity * data[i].unitPrice);

        if (discountValue) {
          if (data[i]?.lineDiscountType === 'Percent') {
            currentDiscount += (data[i].quantity * data[i].unitPrice) * (data[i].discount / 100);
            currentBase += (data[i].quantity * data[i].unitPrice) * (Number(100 - data[i].discount) / 100);
          } else if (data[i]?.lineDiscountType === 'Amount') {
            currentDiscount += discountValue;
            currentBase += (data[i].quantity * data[i].unitPrice) - discountValue;
          }
        } else {
          currentDiscount += 0;
          currentBase += (data[i].quantity * data[i].unitPrice);
        }
      }

      // Subtract TCS from currentBase
      if (taxTypeInfo && taxTypeName === 'TCS') {
        const tcsRate = taxTypeInfo?.tax_value / 100;
        const tcsAmount = currentBase * tcsRate;
        totalTcs += tcsAmount;
      }

      if (data[i].taxId && data[i].taxInfo?.tax_value) currentTax += currentBase * (data?.[i]?.taxId ? data[i].taxInfo?.tax_value / 100 : 0);
      if (currentBase) currentDN = currentBase;

      totalAmount += currentAmount;
      totalDiscount += currentDiscount;
      totalBase += currentBase;
      totalTax += currentTax;
      dnTotal += currentDN;
    }

    // if (freightCharge) {
    //   if (freightTax) {
    //     freightTaxAmount = Helpers.groupAndSumByTaxName([...freightTaxData?.child_taxes?.flat()])?.reduce((acc, curr) => acc + curr?.tax_amount, 0);
    //   }
    //   totalBase += freightCharge;
    //   freightCharge += freightTaxAmount;
    //   if (taxTypeInfo && taxTypeName === 'TCS') {
    //     const tcsRate = taxTypeInfo?.tax_value / 100;
    //     totalTcs = (totalBase + totalTaxValue + Number(totalOtherCharge) + (freightTax ? freightTaxAmount : freightCharge)) * tcsRate;
    //   }
    // }

    if (freightTax) {
      freightTaxAmount = Helpers.groupAndSumByTaxName([...freightTaxData?.child_taxes?.flat()])?.reduce((acc, curr) => acc + curr?.tax_amount, 0);

      totalBase += freightCharge;
      freightCharge += freightTaxAmount;
      if (taxTypeInfo && taxTypeName === 'TCS') {
        const tcsRate = taxTypeInfo?.tax_value / 100;
        totalTcs = totalBase * tcsRate;
      }
    }

    // Add TCS from to total amount including GST ,Freight Other charges
    if (taxTypeInfo && taxTypeName === 'TCS') {
      const tcsRate = taxTypeInfo?.tax_value / 100;

      // If we have freight tax then add only freight tax amount as freight charge is already included in totalBase or if freight tax  is not present add freightCharge
      const tcsAmount = (totalBase + totalTaxValue + Number(totalOtherCharge) + (freightTax ? freightTaxAmount : freightCharge)) * tcsRate;
      totalTcs = tcsAmount;
    }

    dnTotal += totalTcs;
    dnTotal += totalOtherCharge;
    dnTotal += totalTaxValue;
    dnTotal += freightCharge;
    totalBase += totalOtherChargesForTaxableAmount;
    return {
      totalAmount,
      totalDiscount,
      totalBase,
      totalTcs,
      totalTax,
      dnTotal,
    };
  }

  handleProductChange = (tenantSku, key) => {
    const { data, billingAddress, shippingAddress, selectedSelerInfo } = this.state;
    const { getTenantSkuOffer, user, taxesGroup } = this.props;

    const isVendorOverseas = (selectedSelerInfo?.seller_type === 'OVERSEAS') && user?.tenant_info?.purchase_config?.sub_modules?.vendor?.settings?.hide_tax_for_overseas;

    getTenantSkuOffer(tenantSku?.tenant_product_id, null, (sellerOffer) => {
      const copyData = JSON.parse(JSON.stringify(data));
      for (let i = 0; i < copyData?.length; i++) {
        if (copyData[i].key === key) {
          const copyDataItem = copyData[i];
          copyDataItem.asset1 = tenantSku?.product_info?.assets?.[0]?.url || '';
          copyDataItem.product_sku_name = tenantSku?.product_info.product_sku_name;
          copyDataItem.product_sku_id = tenantSku?.product_info.product_sku_id;
          copyDataItem.internal_sku_code = tenantSku?.product_info.internal_sku_code;
          copyDataItem.product_sku_info = tenantSku?.product_info;
          copyDataItem.expiryDateFormat = tenantSku?.expiry_date_format;
          copyDataItem.manufacturingDateFormat = tenantSku?.manufacturing_date_format;
          copyDataItem.tenant_product_id = `${tenantSku?.tenant_product_id}`;
          copyDataItem.quantity = 1;
          copyDataItem.returned_quantity = 1;
          copyDataItem.unitPrice = sellerOffer?.offer_price || 1;
          copyDataItem.offerPrice = sellerOffer?.offer_price || 1;
          copyDataItem.uomId = tenantSku?.uom_id;
          copyDataItem.uom_info = tenantSku?.uom_info;
          copyDataItem.productCategoryInfo = tenantSku?.product_category_info || {};
          // copyDataItem.product_batches = tenantSku?.product_batches; make batch_in_use true in first batch
          // copyDataItem.product_batches = tenantSku?.product_batches?.map((batch, index) => ({
          //   ...batch,
          //   batch_in_use: index === 0,
          //   quantity: index === 0 ? 1 : 0,
          // }));
          copyDataItem.product_batches = this.getBatches(tenantSku?.product_batches, tenantSku?.uom_info, tenantSku?.product_info?.uom_info, null, true, 1);
          copyDataItem.uomGroup = tenantSku?.group_id;
          copyDataItem.taxId = isVendorOverseas ? taxesGroup?.data?.find((tax) => tax?.tax_value === 0)?.tax_id : tenantSku?.tax_info?.tax_id;
          copyDataItem.taxInfo = isVendorOverseas ? taxesGroup?.data?.find((tax) => tax?.tax_value === 0) : tenantSku?.tax_info;
          copyDataItem.child_taxes = Helpers.computeTaxation(1, isVendorOverseas ? taxesGroup?.data?.find((tax) => tax?.tax_value === 0) : tenantSku?.tax_info, this.user?.tenant_info?.state, billingAddress?.state)?.tax_info?.child_taxes;
          copyDataItem.discount = 0;
          copyDataItem.lineDiscountType = 'Percent';
          copyDataItem.hsn_code = tenantSku?.product_info?.hsn_code || '';
          copyData[i] = copyDataItem;
          copyData[i].batchConsumptionMethod = Helpers.getBatchMethod(tenantSku);
        }
      }
      this.setState({ data: copyData });
    });
  }

  handleProductChangeValue = (value, key) => {
    const { data } = this.state;
    const copyData = JSON.parse(JSON.stringify(data));
    for (let i = 0; i < copyData?.length; i++) {
      if (copyData[i].key === key) {
        const newBatches = [];
        const batches = value?.product_batches;
        for (let j = 0; j < batches?.length; j++) {
          newBatches.push({
            ...batches[i],
            batch_in_use: true,
          });
        }
        const copyDataItem = copyData[i];
        copyDataItem.product_sku_name = value;
        copyData[i] = copyDataItem;
      }
    }
    this.setState({ data: copyData });
  }

  addNewRow() {
    const { data } = this.state;
    const copyData = JSON.parse(JSON.stringify(data));
    copyData.push({
      key: uuidv4(),
      asset1: '',
      product_sku_name: '',
      quantity: '',
      returned_quantity: '',
      unitPrice: '',
      taxId: 0,
      lot: '',
      lineDiscountType: 'Percent',
      child_taxes: [{
        tax_amount: 0,
        tax_type_name: '',
      }],
      offerPrice: '',
      discount: 0,
      lineCustomFields: [],
    });
    this.setState({ data: copyData, isAdhocDnLines: true });
  }

  addNewChargesRow() {
    const { chargeData } = this.state;
    const copychargeData = JSON.parse(JSON.stringify(chargeData));
    copychargeData.push({
      chargeKey: uuidv4(),
      charge_name: '',
      charge_amount: 0,
    });
    this.setState({ chargeData: copychargeData });
  }

  isDataValid() {
    const {
      data, allStock, isAdhocDnLines, selectedGrn, debitNoteWithReturnedQyt, debitNoteWithoutLines, tenantDepartmentId, purchaseAccount,
    } = this.state;

    const { user } = this.props;

    let isDataValid = true;
    for (let i = 0; i < data.length; i++) {
      const isStorable = !['NON_STORABLE', 'SERVICE'].includes(data[i]?.product_sku_info?.product_type);

      const basicValidation = Number(data[i].quantity) <= 0
        || !data[i].product_sku_name
        || Number(data[i]?.unitPrice) <= 0
        || !data[i].taxId;

      const quantityValidation = ((Number(data[i].grnRecivedQty || 0) - Number(data[i]?.existingDnQuantity || 0)) < (Number(data[i].quantity)));

      const withReturnedQtyValidation = debitNoteWithReturnedQyt ? ((Number(data[i].quantity) < Number(data[i].returned_quantity)) || Number(data[i].returned_quantity) < 0) : false;

      const withReturnedQtyValidationSecond = debitNoteWithReturnedQyt ? (data[i]?.product_batches?.length === 0 && Number(data[i].returned_quantity) > 0) : data[i]?.product_batches?.length === 0;

      if (isStorable && selectedGrn) {
        const batchQuantity = Helpers.getValueTotalInObject(data[i]?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'consumed_qty');

        const storableValidation = !batchQuantity || (Number(data[i].quantity) > batchQuantity);

        if (!tenantDepartmentId || basicValidation || quantityValidation || storableValidation || withReturnedQtyValidation) {
          isDataValid = false;
          break;
        }
      } else if (!isStorable && selectedGrn) {
        if (!tenantDepartmentId || basicValidation || quantityValidation || withReturnedQtyValidation) {
          isDataValid = false;
          break;
        }
      } else if (isAdhocDnLines && isStorable && !debitNoteWithoutLines) {
        const batchQuantity = Helpers.getValueTotalInObject(data[i]?.product_batches?.filter((item) => (item?.batch_in_use && (allStock ? !item?.is_rejected_batch : true))), 'available_qty');

        const storableValidation = !batchQuantity || (Number(data[i].quantity) > batchQuantity);
        if (!tenantDepartmentId || basicValidation || storableValidation || withReturnedQtyValidation) {
          isDataValid = false;
          break;
        }
      } else if (isAdhocDnLines && !isStorable && !debitNoteWithoutLines) {
        if (!tenantDepartmentId || basicValidation || withReturnedQtyValidation) {
          isDataValid = false;
          break;
        }
      } else if (!tenantDepartmentId || basicValidation) {
        isDataValid = false;
        break;
      }
    }
    const { docLevelError, lineLevelError } = this.getDnErrors();
    if (!purchaseAccount && user?.tenant_info?.tally_configuration?.debit_note_account_selection === 'DOC_LEVEL') {
      isDataValid = false;
    }

    if (docLevelError?.length > 0 || lineLevelError?.length > 0) {
      return false;
    }
    return isDataValid;
  }

  isDataValid2() {
    const { chargeData } = this.state;
    let isDataValid = true;
    if (chargeData?.length) {
      for (let i = 0; i < chargeData?.length; i++) {
        if (!chargeData[i].charge_name || !chargeData[i].charge_amount) {
          isDataValid = false;
        }
      }
    }
    return isDataValid;
  }

  createDN(withApproval, applyDebitNote) {
    this.setState({ withApproval, actionType: withApproval ? (applyDebitNote ? 'ISSUE_AND_APPLY_CREDIT' : 'SAVE_AND_ISSUE') : 'SAVE_AS_DRAFT' });
    const {
      createDN, history, user, match, updateDN, callback, selectedGrnForDN, isQuickView, dnFormFromAPInvoice,
    } = this.props;
    const {
      dnDate, data, shippingAddress, selectedDN, discountPercentage, isLineWiseDiscount, chargeData, selectedCurrencyName, tenantDepartmentId, charge1Name, charge1Value, discountType,
      selectedGrn, termsAndConditions, selectedSeller, selectedSelerInfo, fileList, gstNumber, cfDebitNoteDoc, toRecipients,
      checkedRecipients, billingAddress, taxTypeInfo, taxType, isAutomaticConversionRate, currencyConversionRate, debitNoteWithReturnedQyt, debitNoteWithoutLines, isAdhocDnLines, narration, selectedTenantTallyIntegrationId, purchaseAccount, freightTaxId, freightTaxInfo, freightSacCode, initialDnNumber, dnNumber, docSeqId
    } = this.state;

    const dnLines = [];
    this.setState({ formSubmitted: true, data: JSON.parse(JSON.stringify(data)) });
    if (this.isDataValid() && this.isDataValid2() && selectedSeller && shippingAddress && (isAdhocDnLines ? true : selectedGrn) && billingAddress && dnDate && (checkedRecipients ? toRecipients?.length : true) && (isQuickView || (gstNumber ? String(gstNumber)?.length === 15 : true)) && CustomFieldHelpers.isCfValid(cfDebitNoteDoc)) {
      if (match?.params?.debitNoteId) {
        for (let i = 0; i < data.length; i++) {
          const dnBatches = data?.[i].product_batches;
          // const dnBatches = [];
          // for (let j = 0; j < data?.[i].product_batches?.length; j++) {
          //   dnBatches.push({
          //     batch_id: data?.[i].product_batches[j]?.batch_id,
          //     is_rejected_batch: !!data?.[i].product_batches[j]?.is_rejected_batch,
          //     quantity: data?.[i].product_batches[j]?.quantity,
          //     inventory_location_id: data?.[i].product_batches[j]?.inventory_location_id,
          //   });
          // }
          const dnLine = {
            tenant_product_id: data[i].tenant_product_id,
            product_sku_name: data[i].product_sku_name,
            quantity: Number(data[i].quantity),
            returned_qty: debitNoteWithoutLines ? 0 : (debitNoteWithReturnedQyt ? Number(data[i].returned_quantity) : Number(data[i].quantity)),
            uom_id: Number(data[i]?.uom_info?.uom_id),
            uom_info: [data[i]?.uom_info],
            unit_price: parseFloat(data[i].unitPrice),
            offer_price: parseFloat(data[i].unitPrice),
            tax_id: data[i]?.taxId,
            tax_group_info: data[i]?.taxInfo,
            line_discount_percentage: data?.[i]?.lineDiscountType === 'Percent' ? Number(data?.[i]?.discount || 0) : ((Number(data?.[i]?.discount || 0) / (Number(data[i].quantity) * parseFloat(data?.[i].unitPrice))) * 100),
            is_discount_in_percent: data?.[i]?.lineDiscountType === 'Percent',
            remarks: data[i]?.remarks?.replace(/<[^>]+>/g, ''),
            grnRecivedQty: Number(data?.received_qty),
            dn_line_id: data[i]?.dnLineId,
            product_batches: debitNoteWithoutLines ? [] : dnBatches?.map((batch) => ({
              ...batch,
              quantity: debitNoteWithReturnedQyt ? Number(batch.returned_quantity) : Number(batch.quantity),
              manufacturing_date: batch?.manufacturing_date ? FormHelpers.dateFormatter(batch?.manufacturing_date, data[i]?.manufacturingDateFormat) : null,
              expiry_date: batch?.expiry_date ? FormHelpers.dateFormatter(batch?.expiry_date, data[i]?.expiryDateFormat) : null,
            })),
            update_stock_qty: data[i]?.updateStock,
            default_outwards_method: data[i]?.batchConsumptionMethod,
          };
          dnLines.push(dnLine);
        }
        const payload = [{
          dn_id: selectedDN?.dn_id,
          tenant_seller_id: selectedSeller,
          tenant_id: selectedDN?.tenant_info?.tenant_id || user?.tenant_info?.tenant_id,
          dn_date: dayjs(dnDate).format('YYYY/MM/DD'),
          billing_address: billingAddress?.address_id,
          shipping_address: shippingAddress?.address_id,
          tenant_seller_info: { ...selectedSelerInfo, gst_number: gstNumber, internal_slr_code: selectedDN?.internal_slr_code, seller_type: selectedDN?.tenant_seller_info?.seller_type },
          tenant_department_id: tenantDepartmentId,
          attachments: fileList?.map((attachment) => ({
            url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
          })) || [],
          status: withApproval ? 'CONFIRMED' : 'DRAFT',
          tenant_info: selectedDN?.tenant_info || user?.tenant_info,
          shipping_address_info: shippingAddress,
          billing_address_info: billingAddress,
          grn_id: selectedDN?.ap_invoice_id ? selectedDN?.ap_invoice_id : (selectedGrn || null),
          ap_invoice_id: selectedDN?.ap_invoice_id || null,
          dn_lines: dnLines,
          terms_and_conditions: global.isBlankString(termsAndConditions) ? '' : termsAndConditions,
          custom_fields: CustomFieldHelpers.postCfStructure(cfDebitNoteDoc),
          other_charges: chargeData?.map((charge) => ({
            charge_name: charge?.charge_name,
            charge_amount: Number(charge?.charge_amount),
            charge_type: '',
            charge_sac_code: charge?.chargesSacCode || null,
            tax_info: charge?.chargesTaxInfo || null,
            ledger_name: charge?.tallyLedgerName || null,
          })) || [],
          notification_recipients: toRecipients,
          is_automatic_notification_enabled: checkedRecipients,
          discount_percentage: discountType === 'Percent' ? discountPercentage : (discountPercentage / (this.getLineTotals().totalAmount)) * 100,
          is_line_wise_discount: !isLineWiseDiscount,
          tcs_id: taxTypeInfo ? taxType : null,
          tcs_info: taxTypeInfo ? {
            tax_id: taxTypeInfo?.tax_id,
            tax_name: taxTypeInfo?.tax_name,
            tax_value: taxTypeInfo?.tax_value,
            tax_type_name: taxTypeInfo?.tax_type_name,
          } : null,
          conversion_rate: Number(currencyConversionRate) || Number(selectedCurrencyName?.conversion_rate),
          org_currency_id: Number(selectedCurrencyName?.org_currency_id),
          charge_1_name: charge1Name,
          charge_1_value: Number(charge1Value),
          freight_tax_id: freightTaxId === 'Not Applicable' ? null : freightTaxId,
          freight_tax_info: freightTaxInfo,
          freight_sac_code: freightSacCode,
          is_discount_in_percent: discountType === 'Percent',
          without_lines: debitNoteWithoutLines || false,
          bypass_inventory_change: debitNoteWithReturnedQyt || false,
          narration: narration || '',
          tally_purchase_return_account: purchaseAccount,
          debit_note_number: initialDnNumber?.toLowerCase()?.trim() === dnNumber?.toLowerCase()?.trim() ? null : dnNumber,
        }];
        updateDN(payload, () => {
          this.setState({ actionType: '' });
          history.push(`/purchase/debit-note/view/${selectedDN?.dn_id}`);
        });
      } else {
        for (let i = 0; i < data.length; i++) {
          const dnBatches = data?.[i].product_batches;
          // const dnBatches = [];
          // for (let j = 0; j < data?.[i].product_batches?.length; j++) {
          //   dnBatches.push({
          //     batch_id: data?.[i].product_batches[j]?.batch_id,
          //     quantity: data?.[i].product_batches[j]?.quantity,
          //     inventory_location_id: data?.[i].product_batches[j]?.inventory_location_id,
          //   });
          // }
          const dnLine = {
            tenant_product_id: data[i]?.tenant_product_id,
            grn_line_id: data[i]?.apInvoiceLineId ? null : data[i]?.grnLineId,
            ap_invoice_line_id: data[i]?.apInvoiceLineId ? data[i]?.apInvoiceLineId : null,
            seller_id: selectedSeller,
            product_sku_name: data[i].product_sku_name,
            quantity: Number(data[i].quantity),
            returned_qty: debitNoteWithoutLines ? 0 : (debitNoteWithReturnedQyt ? Number(data[i].returned_quantity) : Number(data[i].quantity)),
            uom_id: debitNoteWithoutLines ? null : Number(data[i]?.uom_info?.uom_id),
            uom_info: debitNoteWithoutLines ? null : [data[i]?.uom_info],
            unit_price: parseFloat(data[i].unitPrice),
            offer_price: parseFloat(data[i].unitPrice),
            tax_id: data[i]?.taxId,
            tax_group_info: data[i]?.taxInfo,
            remarks: data[i]?.remarks?.replace(/<[^>]+>/g, ''),
            line_discount_percentage: data?.[i]?.lineDiscountType === 'Percent' ? Number(data?.[i]?.discount || 0) : ((Number(data?.[i]?.discount || 0) / (Number(data[i].quantity) * parseFloat(data?.[i].unitPrice))) * 100),
            is_discount_in_percent: data?.[i]?.lineDiscountType === 'Percent',
            product_batches: debitNoteWithoutLines ? [] : dnBatches?.map((batch) => ({
              ...batch,
              quantity: debitNoteWithReturnedQyt ? Number(batch.returned_quantity) : Number(batch.quantity),
              manufacturing_date: batch?.manufacturing_date ? FormHelpers.dateFormatter(batch?.manufacturing_date, data[i]?.manufacturingDateFormat) : null,
              expiry_date: batch?.expiry_date ? FormHelpers.dateFormatter(batch?.expiry_date, data[i]?.expiryDateFormat) : null,
            })),
            update_stock_qty: data[i]?.updateStock,
            default_outwards_method: data[i]?.batchConsumptionMethod,
          };
          dnLines.push(dnLine);
        }
        let tenantSellerInfo = {};
        if (selectedGrnForDN) {
          tenantSellerInfo = selectedGrnForDN?.tenant_seller_info;
        }
        const payload = [{
          tenant_seller_id: selectedSeller,
          tenant_id: selectedGrnForDN?.tenant_info?.tenant_id || user?.tenant_info?.tenant_id,
          dn_date: dayjs(dnDate).format('YYYY/MM/DD'),
          shipping_address: shippingAddress?.address_id,
          billing_address: billingAddress?.address_id,
          attachments: fileList?.map((attachment) => ({
            url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
          })) || [],
          status: withApproval ? 'CONFIRMED' : 'DRAFT',
          tenant_seller_info: selectedGrnForDN ? { ...tenantSellerInfo, gst_number: gstNumber, internal_slr_code: selectedGrnForDN?.internal_slr_code } : { ...selectedSelerInfo, gst_number: gstNumber, internal_slr_code: selectedSelerInfo?.internal_slr_code },
          tenant_department_id: tenantDepartmentId,
          tenant_info: selectedGrnForDN?.tenant_info || user?.tenant_info,
          shipping_address_info: shippingAddress,
          billing_address_info: billingAddress,
          grn_id: dnFormFromAPInvoice ? null : (selectedGrn || null),
          ap_invoice_id: selectedGrn || null,
          dn_lines: dnLines,
          terms_and_conditions: global.isBlankString(termsAndConditions) ? '' : termsAndConditions,
          custom_fields: CustomFieldHelpers.postCfStructure(cfDebitNoteDoc),
          other_charges: chargeData?.map((charge) => ({
            charge_name: charge?.charge_name,
            charge_amount: Number(charge?.charge_amount),
            charge_type: '',
            charge_sac_code: charge?.chargesSacCode || null,
            tax_info: charge?.chargesTaxInfo || null,
            ledger_name: charge?.tallyLedgerName || null,
          })) || [],
          notification_recipients: toRecipients,
          is_automatic_notification_enabled: checkedRecipients,
          discount_percentage: discountType === 'Percent' ? discountPercentage : (discountPercentage / (this.getLineTotals().totalAmount)) * 100,
          is_line_wise_discount: !isLineWiseDiscount,
          tcs_id: taxTypeInfo ? taxType : null,
          tcs_info: taxTypeInfo ? {
            tax_id: taxTypeInfo?.tax_id,
            tax_name: taxTypeInfo?.tax_name,
            tax_value: taxTypeInfo?.tax_value,
            tax_type_name: taxTypeInfo?.tax_type_name,
          } : null,
          conversion_rate: Number(currencyConversionRate) || Number(selectedCurrencyName?.conversion_rate),
          org_currency_id: Number(selectedCurrencyName?.org_currency_id),
          charge_1_name: charge1Name,
          charge_1_value: Number(charge1Value),
          freight_tax_id: freightTaxId === 'Not Applicable' ? null : freightTaxId,
          freight_tax_info: freightTaxInfo,
          freight_sac_code: freightSacCode,
          is_discount_in_percent: discountType === 'Percent',
          without_lines: debitNoteWithoutLines || false,
          bypass_inventory_change: debitNoteWithReturnedQyt || false,
          apply_debit_note: !!applyDebitNote,
          narration: narration || '',
          tally_purchase_return_account: purchaseAccount,
          debit_note_number: initialDnNumber?.toLowerCase()?.trim() === dnNumber?.toLowerCase()?.trim() ? null : dnNumber,
          seq_id: docSeqId || null,
        }];
        createDN(payload, (dn) => {
          this.setState({ actionType: '' });
          if (selectedGrnForDN) {
            callback();
          } else {
            history.push(`/purchase/debit-note/view/${dn?.dn_id}`);
          }
        });
      }
    }
  }

  customInputChange(fieldValue, cfId) {
    const { cfDebitNoteDoc } = this.state;
    const newCustomField = cfDebitNoteDoc.map((customField) => {
      if (customField?.cfId === cfId) {
        if (customField?.fieldType === 'ATTACHMENT') {
          return {
            ...customField,
            fieldValue: fieldValue?.map((attachment) => ({
              url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
            })),

          };
        }
        return {
          ...customField,
          fieldValue,
        };
      }
      return {
        ...customField,
      };
    });
    this.setState({
      cfDebitNoteDoc: newCustomField,
    });
  }

  toggleBatch(record, adjustmentRow) {
    const { data } = this.state;
    for (let i = 0; i < data?.length; i++) {
      if (data[i]?.key === adjustmentRow?.key) {
        const rowBatches = data[i]?.product_batches;
        for (let j = 0; j < rowBatches?.length; j++) {
          if (rowBatches[j]?.batch_id === record?.batch_id) {
            rowBatches[j].batch_in_use = !rowBatches[j]?.batch_in_use;
            // data[i].quantity -= rowBatches[j].quantity;
            const qty = new Decimal(data[i]?.quantity || 0);
            const rowQty = new Decimal(rowBatches[j]?.quantity || 0);
            data[i].quantity = qty.minus(rowQty).toNumber();
            rowBatches[j].quantity = 0;
          }
        }
        break;
      }
    }
    this.setState({ data });
  }

  getDataSource(data) {
    const newData = JSON.parse(JSON.stringify(data));
    return newData;
  }

  render() {
    const {
      createDNLoading, getDNByIdLoading, updateDNLoading, updateDNStatusLoading, user, selectedGrnForDN, isQuickView, MONEY, CurrenciesResults, getCurrenciesLoading, selectedDN, getGRN, grnData, getGRNLoading, priceMasking, cfV2DocDebitNote, dnFormFromAPInvoice
    } = this.props;
    const {
      data, formSubmitted, shippingAddress, termsAndConditions, tenantDepartmentId,
      dnDate, showAddressDrawer, selectedAddressType, billingAddress, actionType, chargeData, selectedCurrencyID, selectedCurrencyName,
      selectedGrn, fileList, gstNumber, selectedSeller, dnNumber, selectedSelerInfo, cfDebitNoteDoc, checkedRecipients, toRecipients, isLineWiseDiscount, discountPercentage, allStock, taxType, taxTypeId, taxTypeName, charge1Name, charge1Value, discountType, isAutomaticConversionRate, currencyConversionRate, debitNoteWithReturnedQyt, debitNoteWithoutLines, isAdhocDnLines, selectedSellerCurrencyInfo, narration, selectedTenantTallyIntegrationId, purchaseAccount, freightTax, freightTaxData, freightTaxInfo, freightTaxId, openFreightTax, freightSacCode, docSeqId,
    } = this.state;
    const debitNote = user?.tenant_info?.purchase_config?.sub_modules?.debit_note?.is_active;
    const accountingGSTTransactionAsPerMaster = user?.tenant_info?.purchase_config?.sub_modules?.vendor?.settings?.gst_details_in_transaction === 'AS_PER_MASTER';

    let copyDocLevelError;
    let copyLineLevelError;
    if (formSubmitted) {
      const { docLevelError, lineLevelError } = this.getDnErrors();
      copyDocLevelError = docLevelError;
      copyLineLevelError = lineLevelError;
    }
    const isApInvoiceEnabled = user?.tenant_info?.purchase_config?.sub_modules?.account_payable_invoice?.settings?.is_enabled && user?.tenant_info?.purchase_config?.sub_modules?.account_payable_invoice?.is_active;
    const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

    const purchaseAccountList = user?.user_tenants?.find((item) => item?.tenant_id === user?.tenant_info?.tenant_id)?.tally_configuration?.purchase_account_list;

    const splitChargesData = (charge) => {
      const chargeWithTaxName = charge?.filter((line) => line?.chargesTaxInfo?.tax_id) || [];
      const chargeWithoutTaxName = charge?.filter((line) => !line?.chargesTaxInfo?.tax_id) || [];
      return { chargeWithTaxName, chargeWithoutTaxName };
    };

    const renderCharges = (charge) => charge?.map((line) => (
      <div
        key={line?.chargeKey}
        className="form-calculator__field"
      >
        <div className="form-calculator__field-name">
          {!user?.tenant_info?.purchase_config?.sub_modules?.purchase_order?.settings?.use_custom_charges ? (
            <div className="select_extra_charge_wrapper">
              <SelectExtraCharge
                containerClassName="orgInputContainer"
                selectedChargeName={line?.charge_name}
                disabled={createDNLoading || updateDNLoading}
                onChange={(value) => {
                  const copyChargeData = JSON.parse(JSON.stringify(chargeData));
                  copyChargeData.map((item) => {
                    if (item?.chargeKey === line?.chargeKey) {
                      item.charge_name = value?.ledger_name;
                      item.chargesSacCode = (value?.charge_sac_code) || null;
                    }
                  });
                  this.setState({ chargeData: copyChargeData });
                }}
                customStyle={{
                  width: '220px',
                  backgroundColor: 'white',
                }}
                excludeCharges={chargeData?.map((item) => item?.charge_name)}
                entityName="PURCHASE"
              />
              {line?.chargesTax && (
                <div style={{
                  color: '#2d7df7',
                  fontWeight: '400',
                  fontSize: '12px',
                }}
                >
                  {`tax@${line?.chargesTax}%`}
                </div>
              )}
            </div>
          ) : (
            <H3FormInput
              value={line?.charge_name}
              type="text"
              containerClassName={`orgInputContainer ${formSubmitted && Number(line?.charge_name) <= 0 ? 'form-error__input' : ''}`}
              placeholder="Charge name.."
              labelClassName="orgFormLabel"
              inputClassName="orgFormInput"
              onChange={(e) => {
                const copyChargeData = JSON.parse(
                  JSON.stringify(chargeData),
                );
                copyChargeData.map((item) => {
                  if (item?.chargeKey === line?.chargeKey) {
                    item.charge_name = e.target.value;
                  }
                  return data;
                });
                this.setState({ chargeData: copyChargeData });
              }}
            />
          )}
        </div>
        <div className="form-calculator__field-value" style={{ display: 'flex', gap: '0px' }}>
          <div style={{ width: '140px', marginRight: '-27px' }}>
            <H3FormInput
              value={line?.charge_amount}
              type="number"
              containerClassName={`orgInputContainer ${formSubmitted && Number(line?.charge_amount) <= 0 ? 'form-error__input' : ''}`}
              labelClassName="orgFormLabel"
              inputClassName="orgFormInput"
              onChange={(e) => {
                const copyChargeData = JSON.parse(JSON.stringify(chargeData));
                copyChargeData.map((item) => {
                  if (item?.chargeKey === line?.chargeKey) {
                    const updatedChargeAmount = parseFloat(e.target.value) || 0;

                    // Update charge_amount
                    item.charge_amount = updatedChargeAmount;
                    // Compute chargesTaxData with updated values
                    const computedTaxData = Helpers.computeTaxation(
                      updatedChargeAmount,
                      line.chargesTaxInfo,
                      billingAddress?.state,
                      shippingAddress?.state,
                    );
                    // Update chargesTaxData
                    item.chargesTaxData = {
                      ...line?.chargesTaxInfo,
                      child_taxes: computedTaxData?.tax_info?.child_taxes || [],
                    };
                  }
                });
                this.setState({ chargeData: copyChargeData });
              }}
              allowNegative
            />
          </div>
          {!isVendorOverseas && (<ChargesTaxInput
            selectedTenant={user?.tenant_info?.tenant_id}
            openChargesTax={line?.openChargesTax}
            setOpenChargesTax={(value) => {
              const copyChargeData = JSON.parse(JSON.stringify(chargeData));
              copyChargeData.map((item) => {
                if (item?.chargeKey === line?.chargeKey) {
                  item.openChargesTax = value;
                }
              });
              this.setState({ chargeData: copyChargeData });
            }}
            sacCode={line?.chargesSacCode}
            setSacCode={(value) => {
              const copyChargeData = JSON.parse(JSON.stringify(chargeData));
              copyChargeData.map((item) => {
                if (item?.chargeKey === line?.chargeKey) {
                  item.chargesSacCode = value;
                }
              });
              this.setState({ chargeData: copyChargeData });
            }}
            chargesTaxId={line?.chargesTaxId}
            setChargesTaxData={(value) => {
              const updatedChargeData = chargeData?.map((item) => {
                if (item?.chargeKey === line?.chargeKey) {
                  return {
                    ...item,
                    chargesTaxId: !value ? 'Not Applicable' : value?.tax_id,
                    chargesTax: !value ? null : value?.tax_value,
                    chargesTaxInfo: !value ? null : value,
                    chargesTaxData: !value ? {
                      child_taxes: [
                        {
                          tax_amount: 0,
                          tax_type_name: '',
                        },
                      ],
                    } : {
                      ...value,
                      child_taxes: Helpers.computeTaxation(line?.charge_amount, value, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes,
                    },
                  };
                }
                return item;
              });
              this.setState({ chargeData: updatedChargeData });
            }}
            showTallyLedgerPurchase
            tallyLedgerName={line?.tallyLedgerName}
            setTallyLedgerName={(value) => {
              const updatedChargeData = chargeData?.map((item) => {
                if (item?.chargeKey === line?.chargeKey) {
                  item.tallyLedgerName = value;
                }
                return item;
              });
              this.setState({ chargeData: updatedChargeData });
            }}
          />)}
          <div
            className="form-calculator__delete-line-button"
            onClick={() => this.handleDeleteCharge(line?.chargeKey)}
          >
            <FontAwesomeIcon
              icon={faCircleXmark}
              size="sm"
              style={{ color: '#6f7276' }}
            />
          </div>
        </div>
      </div>
    ));

    const isVendorOverseas = (selectedSelerInfo?.seller_type === 'OVERSEAS') && user?.tenant_info?.purchase_config?.sub_modules?.vendor?.settings?.hide_tax_for_overseas;

    return (
      <div>

        <Drawer
          open={showAddressDrawer}
          onClose={() => this.setState({ showAddressDrawer: false, selectedAddressType: '' })}
          width="350"
          destroyOnClose
        >
          <AddressSelector
            title={selectedAddressType === 'TENANT_SHIPPING' ? 'Return To' : (isQuickView ? 'Return From' : 'Return/Debit To')}
            selectedAddressId={
              selectedAddressType === 'TENANT_SHIPPING'
                ? shippingAddress?.address_id
                : billingAddress?.address_id
            }
            onAddressChange={(address) => {
              if (selectedAddressType === 'TENANT_SHIPPING') {
                this.setState({
                  shippingAddress: address,
                  showAddressDrawer: false,
                  data: data?.map((record) => {
                    const discountValue = Number(record?.discount) || 0;
                    const taxableValue = record?.is_discount_in_percent
                      ? (record?.quantity * record?.unitPrice) * (1 - discountValue / 100)
                      : Math.max(record.quantity * record?.unitPrice - discountValue, 0);
                    return {
                      ...record,
                      child_taxes: Helpers.computeTaxation(taxableValue, record.taxInfo, billingAddress?.state, address?.state)?.tax_info?.child_taxes,
                    };
                  }),
                });
              } if (selectedAddressType === 'TENANT_BILLING') {
                this.setState({
                  billingAddress: address,
                  showAddressDrawer: false,
                  data: data?.map((record) => {
                    const discountValue = Number(record?.discount) || 0;
                    const taxableValue = record?.is_discount_in_percent
                      ? (record?.quantity * record?.unitPrice) * (1 - discountValue / 100)
                      : Math.max(record.quantity * record?.unitPrice - discountValue, 0);
                    return {
                      ...record,
                      child_taxes: Helpers.computeTaxation(taxableValue, record.taxInfo, address?.state, shippingAddress?.state)?.tax_info?.child_taxes,
                    };
                  }),
                });
              }
            }}
            entityId={selectedAddressType === 'TENANT_SHIPPING' ? selectedSelerInfo?.seller_id || selectedGrnForDN?.seller_id : user?.tenant_info?.tenant_id}
            entityType={selectedAddressType === 'TENANT_SHIPPING' ? 'SELLER' : 'TENANT'}
            tenantId={selectedGrnForDN?.tenant_info?.tenant_id || selectedDN?.tenant_info?.tenant_id || user?.tenant_info?.tenant_id}
          />
        </Drawer>

        {getDNByIdLoading ? (
          <div className="create-dn__loading__wrapper">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div key={item} className="create-dn__loading__wrapper-row">
                <div className="create-dn__loading__wrapper-row__label loadingBlock" />
                <div className="create-dn__loading__wrapper-row__value loadingBlock" />
              </div>
            ))}
            <div className="create-dn__loading__wrapper-table loadingBlock" />
            <div className="create-dn__loading__wrapper-details loadingBlock" />
          </div>
        )
          : (
            <div
              className="form__wrapper"
              style={{ paddingTop: window.location.href.includes('/purchase/debit-notes/') ? '90px' : '0px' }}
            >
              <div className="ant-row">
                <div className="ant-col-md-24">
                  <div className="form__section">
                    {(formSubmitted && (copyDocLevelError?.length > 0 || copyLineLevelError?.length > 0)) && (
                      <ErrorHandle message="Mandatory fields required" docLevelErrors={copyDocLevelError} lineLevelErrors={copyLineLevelError} />
                    )}
                    <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                      <H3Text text="PART A" className="form__section-title" />
                      <div className="form__section-line" />
                      <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                        <CurrencyConversionV2
                          selectedCurrencyName={selectedCurrencyName}
                          selectedCurrencyID={selectedCurrencyID}
                          isAutomaticConversionRate={isAutomaticConversionRate}
                          currencyConversionRate={currencyConversionRate}
                          setCurrencyConversionRate={(val) => this.setState({ currencyConversionRate: val })}
                          setIsAutomaticConversionRate={(val) => this.setState({ isAutomaticConversionRate: val })}
                          setSelectedCurrencyName={(val) => this.setState({ selectedCurrencyName: val })}
                          setSelectedCurrencyID={(val) => this.setState({ selectedCurrencyID: val })}
                        />
                      </div>
                    </div>

                    <div className="form__section-inputs mg-bottom-20">
                      <div className="ant-row">
                        <DocumentNumberSeqInput
                          valueFromProps={dnNumber}
                          updateCase={this.props?.match?.params?.debitNoteId}
                          setInitialDocSeqNumber={(value) => this.setState({ initialDnNumber: value })}
                          entityName="DEBIT_NOTE"
                          docSeqId={docSeqId}
                          tenantId={user?.tenant_info?.tenant_id}
                          onChangeFromProps={(event, newValue, seqId) => {
                            this.setState({
                              dnNumber: newValue ? (newValue || '') : (event?.target?.value || ''),
                              docSeqId: seqId,
                            })
                          }}
                          docTitle="Debit Note #"
                          formSubmitted={formSubmitted}
                        />
                        <div className="ant-col-md-6">
                          <div className="form__input-row">
                            <H3Text
                              text="Vendors"
                              className="form__input-row__label"
                              required
                            />
                            <div className={`form__input-row__input ${(formSubmitted && !selectedSeller) ? 'form__input-row__input-error' : ''}`}>
                              <SelectSeller
                                selectedSeller={selectedSeller}
                                onChange={(value) => {
                                  getGRN(user?.tenant_info?.tenant_id, '', '', '', 'ISSUED', 10, 1, value?.tenant_seller_id);

                                  const docCf = CustomFieldHelpers.postCfStructure(cfDebitNoteDoc?.filter((item) => (item?.isActive && item?.visible))) || [];
                                  const vendorCf = value?.custom_field_values?.filter((item) => (item?.is_active)) || [];
                                  const mergedCf = CustomFieldHelpers.mergeCustomFields(docCf, vendorCf) || [];

                                  this.setState({
                                    selectedSeller: value?.tenant_seller_id,
                                    selectedSelerInfo: value?.seller_info,
                                    gstNumber: value?.seller_info?.gst_number,
                                    shippingAddress: value?.seller_info?.office_address_details,
                                    selectedGrn: null,
                                    tenantDepartmentId: null,
                                    data: [],
                                    isAutomaticConversionRate: user?.tenant_info?.global_config?.settings?.automatic_conversion_rate,
                                    selectedCurrencyID: value?.org_currency_id,
                                    selectedCurrencyName: value?.currency_info,
                                    currencyConversionRate: user?.tenant_info?.global_config?.settings?.automatic_conversion_rate ? value?.currency_info?.automatic_conversion_rate : value?.currency_info?.conversion_rate,
                                    selectedSellerCurrencyInfo: value?.currency_info,
                                    cfDebitNoteDoc: mergedCf,
                                  });
                                }}
                                disabled={selectedGrnForDN || selectedDN?.data?.debit_notes?.[0]?.grn_id}
                                containerClass="create-dn__input-row orgInputContainer"
                                inputClassName=" create-dn__input-row__input"
                                labelClassName="create-dn__input-row__label"
                                showError={formSubmitted && !selectedSeller}
                                hideTitle
                              />
                            </div>
                          </div>
                        </div>
                        {!isQuickView && !isApInvoiceEnabled && (selectedDN ? selectedDN?.data?.debit_notes?.[0]?.grn_id : true) && (
                          <div className="ant-col-md-6">
                            <div className="form__input-row">
                              <H3Text text="GRN" className="form__input-row__label" />
                              <div className="form__input-row__input">
                                <PRZSelect
                                  containerClass="form__input-row"
                                  filterOption={false}
                                  value={selectedGrn}
                                  disabled={createDNLoading || updateDNStatusLoading || selectedGrnForDN || selectedDN?.data?.debit_notes?.[0]?.grn_id}
                                  onChange={(value) => {
                                    if (value) {
                                      const grn = grnData?.grn?.filter((item) => item?.grn_id === value)?.[0];
                                      const grnLines = grnData?.grn?.filter((item) => item?.grn_id === value)?.[0]?.grn_lines?.map((orderLine) => ({
                                        key: uuidv4(),
                                        asset1: orderLine?.product_sku_info?.assets?.[0]?.url || '',
                                        product_sku_name: orderLine?.product_sku_info?.product_sku_name,
                                        product_sku_info: orderLine?.product_sku_info,
                                        offerPrice: Number(orderLine?.offer_price),
                                        lineDiscountType: orderLine?.is_discount_in_percent ? 'Percent' : 'Amount',
                                        discount: orderLine?.is_discount_in_percent ? orderLine?.line_discount_percentage : orderLine?.line_discount_amount,
                                        order_line_id: orderLine?.order_line_id,
                                        tenant_product_id: orderLine?.tenant_product_id,
                                        manufacturingDateFormat: orderLine?.product_sku_info?.manufacturing_date_format,
                                        expiryDateFormat: orderLine?.product_sku_info?.expiry_date_format,
                                        quantity: QUANTITY(orderLine?.received_qty - orderLine?.dn_qty, orderLine?.uom_info?.[0]?.precision),
                                        returned_quantity: QUANTITY(orderLine?.received_qty - orderLine?.dn_qty, orderLine?.uom_info?.[0]?.precision),
                                        unitPrice: Number(orderLine?.offer_price),
                                        grnRecivedQty: Number(orderLine?.received_qty),
                                        existingDnQuantity: Number(orderLine?.dn_qty),
                                        taxId: Number(orderLine?.tax_id),
                                        uomId: orderLine?.product_sku_info?.uom_id ? orderLine?.product_sku_info?.uom_id : '',
                                        uom_info: orderLine?.uom_info?.[0],
                                        productCategoryInfo: orderLine?.product_sku_info?.product_category_info || {},
                                        taxInfo: orderLine?.tax_group_info,
                                        child_taxes: Helpers.computeTaxation(((orderLine?.received_qty - orderLine?.dn_qty) * orderLine.offer_price) * (orderLine.line_discount_percentage ? Number(100 - orderLine.line_discount_percentage) / 100 : 1), orderLine?.tax_group_info, grn?.tenant_info?.default_billing_address_info?.state, grn?.seller_address_info?.state)?.tax_info?.child_taxes,
                                        grnLineId: orderLine?.grn_line_id,
                                        batchId: orderLine?.batch_id,
                                        product_batches: this.getBatches(Helpers.batchSelectionMethod(orderLine?.default_outwards_method || Helpers.getBatchMethod(orderLine), orderLine?.product_batches, orderLine?.quantity), orderLine?.uom_info?.[0]?.ratio, orderLine?.product_sku_info?.ratio, QUANTITY(orderLine?.received_qty - orderLine?.dn_qty, orderLine?.uom_info?.[0]?.precision)),
                                        updateStock: true,
                                        remarks: orderLine?.remarks?.replace(/<[^>]+>/g, ''),
                                        batchConsumptionMethod: Helpers.getBatchMethod(orderLine),
                                      }));
                                      const docCf = cfV2DocDebitNote?.data?.document_custom_fields || [];
                                      const grnCf = grn?.custom_fields?.filter((item) => (item?.is_active)) || [];
                                      const mergedCf = CustomFieldHelpers.mergeCustomFields(docCf, grnCf) || [];

                                      this.setState({
                                        selectedGrn: value,
                                        data: grnLines,
                                        selectedCurrencyID: grn?.org_currency_info?.org_currency_id,
                                        selectedCurrencyName: grn?.org_currency_info,
                                        currencyConversionRate: grn?.conversion_rate,
                                        tenantDepartmentId: grn?.tenant_department_id,
                                        isAutomaticConversionRate: false,
                                        debitNoteWithoutLines: false,
                                        debitNoteWithReturnedQyt: false,
                                        isAdhocDnLines: false,
                                        narration: '',
                                        selectedTenantTallyIntegrationId: user?.user_tenants?.find((item) => item?.tenant_id === grn?.grn_tenant_id)?.it_id,
                                        discountType: grn?.is_discount_in_percent ? 'Percent' : 'Amount',
                                        discountPercentage: grn?.is_discount_in_percent ? grn?.discount_percentage : grn?.discount_amount,
                                        isLineWiseDiscount: !grn?.is_line_wise_discount,
                                        chargeData: grn?.other_charges?.map((item) => ({
                                          ...item,
                                          chargeKey: uuidv4(),
                                          chargesTaxId: item?.tax_info?.tax_id,
                                          chargesSacCode: item?.charge_sac_code,
                                          chargesTaxInfo: item?.tax_info,
                                          chargesTax: item?.tax_info?.tax_value,
                                          chargesTaxData: {
                                            ...item?.tax_info,
                                            child_taxes: Helpers.computeTaxation(item?.charge_amount, item?.tax_info, grn?.tenant_info?.default_billing_address_info?.state, grn?.seller_address_info?.state)?.tax_info?.child_taxes,
                                          },
                                        })),
                                        charge1Name: grn?.charge_1_name,
                                        charge1Value: grn?.charge_1_value,
                                        freightSacCode: grn?.freight_sac_code,
                                        freightTaxId: grn?.freight_tax_id,
                                        freightTaxInfo: grn?.freight_tax_info,
                                        freightTax: grn?.freight_tax_info?.tax_value,
                                        freightTaxData: {
                                          ...grn?.freight_tax_info,
                                          child_taxes: Helpers.computeTaxation(grn?.charge_1_value, grn?.freight_tax_info, grn?.tenant_info?.default_billing_address_info?.state, grn?.seller_address_info?.state)?.tax_info?.child_taxes,
                                        },
                                        cfDebitNoteDoc: mergedCf,
                                      });
                                    } else {
                                      this.setState({
                                        selectedGrn: null,
                                        data: [],
                                        selectedCurrencyID: selectedSellerCurrencyInfo?.org_currency_id,
                                        selectedCurrencyName: selectedSellerCurrencyInfo,
                                        currencyConversionRate: user?.tenant_info?.global_config?.settings?.automatic_conversion_rate ? selectedSellerCurrencyInfo?.automatic_conversion_rate : selectedSellerCurrencyInfo?.conversion_rate,
                                        isAutomaticConversionRate: user?.tenant_info?.global_config?.settings?.automatic_conversion_rate,
                                        tenantDepartmentId: null,
                                        debitNoteWithoutLines: false,
                                        debitNoteWithReturnedQyt: false,
                                        isAdhocDnLines: false,
                                        selectedTenantTallyIntegrationId: user?.tenant_info?.it_id,
                                        narration: '',
                                      });
                                    }
                                  }}
                                  allowClear
                                >
                                  {grnData && grnData?.grn?.map((item) => (
                                    <Option
                                      key={item?.grn_id}
                                      value={item?.grn_id}
                                    >
                                      {`#${item.grn_number} - `}
                                      <b>{ISTDateFormat(item.created_at, 'MMM DD, YY')}</b>
                                      {' - '}
                                      <b>
                                        {item.grn_lines?.length}
                                        {' '}
                                        Items
                                      </b>
                                      {' - '}
                                      <b>{(isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver /> : MONEY((item.grn_grand_total), item?.org_currency_info?.currency_code)}</b>
                                    </Option>
                                  ))}
                                  {selectedDN && selectedDN?.data?.debit_notes?.map((item) => (
                                    <Option
                                      key={item?.grn_id}
                                      value={item?.grn_id}
                                    >
                                      {`#${item.grn_number} - `}
                                      <b>{ISTDateFormat(item.grn_date_time, 'MMM DD, YY')}</b>
                                      {' - '}
                                      <b>{MONEY((item.grn_grand_total), item?.org_currency_info?.currency_code)}</b>
                                    </Option>
                                  ))}
                                </PRZSelect>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="ant-col-md-6">
                          <div className="form__input-row">
                            <H3Text
                              required
                              text="Department"
                              className="form__input-row__label"
                            />
                            <div className={`form__input-row__input ${(formSubmitted && !tenantDepartmentId) ? 'form__input-row__input-error' : ''}`}>
                              <SelectDepartment
                                hideTitle
                                tenantId={selectedDN?.data?.debit_notes?.[0]?.tenant_id || selectedGrnForDN?.grn_tenant_id || selectedGrn?.grn_tenant_id}
                                selectedDepartment={tenantDepartmentId}
                                noDropdownAlign
                                onChange={(value) => {
                                  this.setState({
                                    tenantDepartmentId: value?.tenant_department_id,
                                    data: [{
                                      key: uuidv4(),
                                      asset1: '',
                                      product_sku_name: '',
                                      quantity: '',
                                      offerPrice: '',
                                      unitPrice: '',
                                      lot: 0,
                                      taxId: '',
                                      discount: 0,
                                      child_taxes: [{
                                        tax_amount: 0,
                                        tax_type_name: '',
                                      }],
                                      lineCustomFields: [],
                                    }],
                                    isAdhocDnLines: true,
                                  });
                                }}
                                tenentLevelDepartment
                                emptyNotAllowed
                                containerClassName="create-dn__input-row__input"
                                inputClassName="orgFormInput input"
                                labelClassName="form__input-row__label"
                                loading={createDNLoading || updateDNLoading}
                                disabled={createDNLoading || updateDNLoading || selectedGrnForDN || selectedGrn || !selectedSeller}
                              />

                            </div>
                          </div>
                        </div>

                        {!isQuickView && (
                          <div className="ant-col-md-6">
                            <div className="form__input-row">
                              <H3Text
                                text="GST Number"
                                className="form__input-row__label"
                              />
                              <div className="form__input-row__input">
                                <H3FormInput
                                  name="GST Number"
                                  type="text"
                                  containerClassName="orgInputContainer create-dn__input-row__input"
                                  labelClassName="orgFormLabel"
                                  inputClassName="orgFormInput input"
                                  disabled={accountingGSTTransactionAsPerMaster}
                                  placeholder=""
                                  onChange={(e) => this.setState({ gstNumber: e.target.value })}
                                  value={gstNumber}
                                  showError={formSubmitted && (gstNumber ? String(gstNumber)?.length !== 15 : false)}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* <div className="ant-col-md-6">
                          <div className="form__input-row orgInputContainer">
                            <H3Text text="Debit Note #" className="form__input-row__label" />
                            <div className="form__input-row__input">
                              <H3FormInput
                                name="Debit Note #"
                                type="text"
                                disabled
                                containerClassName="orgInputContainer create-dn__input-row__input"
                                labelClassName="orgFormLabel"
                                inputClassName="orgFormInput input"
                                placeholder=""
                                value={dnNumber}
                              />
                            </div>
                          </div>
                        </div> */}

                        <div className="ant-col-md-6">
                          <div className="form__input-row orgInputContainer">
                            <H3Text
                              required
                              text="Debit Note Date"
                              className="form__input-row__label"
                            />
                            <div className={`form__input-row__input ${(formSubmitted && !dnDate) ? 'form__input-row__input-error' : ''}`}>
                              <DatePicker
                                value={dnDate}
                                onChange={(value) => {
                                  this.setState({ dnDate: value });
                                }}
                                style={{
                                  border: '1px solid rgba(68, 130, 218, 0.2)',
                                  borderRadius: '2px',
                                  height: '28px',
                                  padding: '1px 3px',
                                  width: '100%',
                                  background: 'white',
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {user?.tenant_info?.tally_configuration?.debit_note_account_selection === 'DOC_LEVEL' && (
                          <div className="ant-col-md-6">
                            <div className="form__input-row">
                              <H3Text text="Purchase Account" className="form__input-row__label" required />
                              <div className={`form__input-row__input ${formSubmitted && !purchaseAccount && 'form__input-row__input-error'}`}>
                                <PRZSelect
                                  value={purchaseAccount}
                                  onChange={(value) => {
                                    this.setState({ purchaseAccount: value });
                                  }}
                                  loading={createDNLoading || updateDNLoading}
                                  disabled={updateDNLoading}
                                >
                                  {purchaseAccountList?.map((item) => (
                                    <Option key={item.purchase_account_name} value={item.purchase_account_name}>
                                      {' '}
                                      {item.purchase_account_name}
                                    </Option>
                                  ))}
                                </PRZSelect>

                              </div>
                            </div>
                          </div>
                        )}

                        <CustomFieldV3
                          customFields={cfDebitNoteDoc || []}
                          formSubmitted={formSubmitted}
                          customInputChange={(value, cfId) => this.customInputChange(value, cfId)}
                          wrapperClassName="ant-col-md-6"
                          containerClassName="form__input-row"
                          labelClassName="form__input-row__label"
                          inputClassName="form__input-row__input"
                          errorClassName="form__input-row__input-error"
                          disableCase={createDNLoading || updateDNStatusLoading || updateDNLoading}
                          hideTitle
                          isCarryForward={true}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form__section">
                    <div className="flex-display flex-align-c mg-bottom-5 pd-right-15">
                      <H3Text text="PART B" className="form__section-title" />
                      <div className="form__section-line" />
                    </div>
                    <div className="ant-row">
                      <div className="ant-col-md-8">
                        <div className="form__input-row">
                          <H3Text
                            required
                            text={isQuickView ? 'Return From' : 'Return/Debit To'}
                            className="form__input-row__label"
                          />

                          <div className={`form__input-row__address__wrapper ${(formSubmitted && !billingAddress) ? 'form__input-row__address-error' : ''} ${(!selectedSeller) ? 'form__input-row__address-disabled' : ''}`}>
                            <div className="form__input-row__address">
                              {billingAddress && (
                                <div className="form__input-row__address-info">
                                  <div className="form__input-row__address-l1">{billingAddress?.address1}</div>
                                  <div
                                    className="form__input-row__address-l2"
                                  >
                                    {`${billingAddress?.city}, ${billingAddress?.state}, ${billingAddress?.postal_code}, ${billingAddress?.country}`}
                                  </div>

                                </div>
                              )}
                              {!billingAddress && <H3Text text="Select address.." className="form__input-row__address-placeholder" />}
                              <div
                                className={`form__input-row__address-icon ${!selectedSeller && 'pointer-none'}`}
                                onClick={() => selectedSeller && this.setState({ selectedAddressType: 'TENANT_BILLING', showAddressDrawer: true })}
                              >
                                <EditFilled />
                                {' '}
                                Update
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="ant-col-md-8">
                        <div className="form__input-row">
                          <H3Text
                            required
                            text="Return To"
                            className="form__input-row__label"
                          />
                          <div className={`form__input-row__address__wrapper ${(formSubmitted && !shippingAddress) ? 'form__input-row__address-error' : ''} ${(!selectedSeller) ? 'form__input-row__address-disabled' : ''}`}>
                            <div className="form__input-row__address">
                              {shippingAddress && (
                                <div className="form__input-row__address-info">
                                  <div className="form__input-row__address-l1">{shippingAddress?.address1}</div>
                                  <div
                                    className="form__input-row__address-l2"
                                  >
                                    {`${shippingAddress?.city}, ${shippingAddress?.state}, ${shippingAddress?.postal_code}, ${shippingAddress?.country}`}
                                  </div>
                                </div>
                              )}
                              {!shippingAddress && <H3Text text="Select shipping address.." className="form__input-row__address-placeholder" />}
                              <div
                                className={`form__input-row__address-icon ${!selectedSeller && 'pointer-none'}`}
                                onClick={() => {
                                  // eslint-disable-next-line no-unused-expressions
                                  selectedSeller && this.setState({ selectedAddressType: 'TENANT_SHIPPING', showAddressDrawer: true });
                                }}
                              >
                                <EditFilled />
                                {' '}
                                Update
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="ant-col-md-8" />
                      <div className="ant-col-md-8 form__checkboxes">
                        <div className="form__input-row">
                          <Checkbox
                            disabled={createDNLoading || updateDNStatusLoading || !selectedSeller || debitNoteWithoutLines || !tenantDepartmentId}
                            checked={debitNoteWithReturnedQyt}
                            onChange={(e) => {
                              let copyData = JSON.parse(JSON.stringify(data));

                              if (selectedDN) {
                                copyData = copyData?.map((item) => {
                                  if (item?.product_batches) {
                                    const batches = JSON.parse(JSON.stringify(item?.product_batches));

                                    if (!e.target.checked) {
                                      let remainingQty = parseFloat(item?.quantity);

                                      for (let i = 0; i < batches?.length; i++) {
                                        batches[i].quantity = 0;
                                      }

                                      if (isAdhocDnLines) {
                                        for (let i = 0; i < batches?.length; i++) {
                                          if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                                            if (remainingQty > 0 && parseFloat(batches[i].available_qty || '0') > Number(remainingQty || '0')) {
                                              batches[i].quantity = remainingQty;
                                              remainingQty = 0;
                                            } else if (remainingQty > 0 && parseFloat(batches[i].available_qty || '0') <= Number(remainingQty || '0')) {
                                              batches[i].quantity = batches[i].available_qty;
                                              remainingQty -= batches[i].available_qty;
                                            } else {
                                              batches[i].quantity = 0;
                                              remainingQty -= batches[i].quantity;
                                            }
                                          }
                                        }
                                      } else {
                                        for (let i = 0; i < batches?.length; i++) {
                                          if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                                            if (remainingQty > 0 && parseFloat(batches[i].quantity || '0') < Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0))) {
                                              batches[i].quantity = Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0)) > remainingQty ? remainingQty : Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0));
                                              remainingQty -= batches[i].quantity;
                                            } else {
                                              batches[i].quantity = 0;
                                              remainingQty -= batches[i].quantity;
                                            }
                                          }
                                        }
                                      }
                                    } else {
                                      let remainingQty = parseFloat(item?.returned_quantity);

                                      for (let i = 0; i < batches?.length; i++) {
                                        batches[i].returned_quantity = 0;
                                      }

                                      if (isAdhocDnLines) {
                                        for (let i = 0; i < batches?.length; i++) {
                                          if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                                            if (remainingQty > 0 && parseFloat(batches[i].available_qty || '0') > Number(remainingQty || '0')) {
                                              batches[i].returned_quantity = remainingQty;
                                              remainingQty = 0;
                                            } else if (remainingQty > 0 && parseFloat(batches[i].available_qty || '0') <= Number(remainingQty || '0')) {
                                              batches[i].returned_quantity = batches[i].available_qty;
                                              remainingQty -= batches[i].available_qty;
                                            } else {
                                              batches[i].returned_quantity = 0;
                                              remainingQty -= batches[i].returned_quantity;
                                            }
                                          }
                                        }
                                      } else {
                                        for (let i = 0; i < batches?.length; i++) {
                                          if (batches[i]?.batch_in_use && (allStock ? !batches[i]?.is_rejected_batch : true)) {
                                            if (remainingQty > 0 && parseFloat(batches[i].returned_quantity || '0') < Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0))) {
                                              batches[i].returned_quantity = Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0)) > remainingQty ? remainingQty : Number(batches?.[i]?.consumed_qty - (batches?.[i]?.dn_gen_qty || 0));
                                              remainingQty -= batches[i].returned_quantity;
                                            } else {
                                              batches[i].returned_quantity = 0;
                                              remainingQty -= batches[i].returned_quantity;
                                            }
                                          }
                                        }
                                      }
                                    }

                                    let batchQuantity;

                                    if (!e.target.checked) {
                                      batchQuantity = batches?.map((batch) => (batch.batch_in_use ? { ...batch, returned_quantity: batch.quantity } : batch));
                                    } else {
                                      batchQuantity = batches;
                                    }

                                    return {
                                      ...item,
                                      product_batches: batchQuantity,
                                      returned_quantity: item?.quantity,
                                    };
                                  }
                                  return {
                                    ...item,
                                    returned_quantity: item?.quantity,
                                  };
                                });
                              } else {
                                copyData = copyData?.map((i) => {
                                  if (i?.product_batches) {
                                    let productBatches = JSON.parse(JSON.stringify(i?.product_batches));
                                    productBatches = productBatches?.map((batch) => ({ ...batch, returned_quantity: batch?.quantity }));
                                    return {
                                      ...i,
                                      product_batches: productBatches,
                                      returned_quantity: i?.quantity,
                                    };
                                  }
                                  return {
                                    ...i,
                                    returned_quantity: i?.quantity,
                                  };
                                });
                              }

                              this.setState({
                                debitNoteWithReturnedQyt: e.target.checked,
                                data: copyData,
                              });
                            }}
                          />
                          <span
                            style={{
                              fontWeight: '500',
                              fontSize: '12px',
                              marginLeft: '5px',
                            }}
                          >
                            Credit Only Goods
                            <Tooltip
                              title={(
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                  <div>
                                    The quantity specified in the return may differ from the actual quantity on the debit note. It is also possible to create a debit note for the vendor with a return quantity of zero.
                                  </div>
                                  <br />
                                  <div>
                                    <b>Examples:</b>
                                  </div>
                                  <br />
                                  <div>
                                    <i>Example 1:</i>
                                    {' '}
                                    A customer returns 5 units of a product to the vendor due to defects. However, after negotiation, a debit note is created for 3 units only. The remaining 2 units might be adjusted in future transactions or handled separately.
                                  </div>
                                  <br />
                                  <div>
                                    <i>Example 2:</i>
                                    {' '}
                                    A vendor has issued defective goods, but instead of physically returning the items, the buyer chooses to create a debit note for the full amount or partial amount with a return quantity of zero. This allows the buyer to claim a credit without actually returning any items.
                                  </div>
                                </div>
                              )}
                            >
                              <InfoCircleOutlined
                                style={{ marginLeft: '5px', color: 'rgb(45, 124, 247)', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </span>
                        </div>
                        <div className="form__input-row">
                          <Checkbox
                            // disabled={createCNLoading || updateCNStatusLoading || !selectedCustomer || selectedInvoice}
                            disabled={createDNLoading || updateDNLoading || selectedGrnForDN || selectedGrn || !selectedSeller || !tenantDepartmentId}
                            checked={debitNoteWithoutLines}
                            onChange={(e) => {
                              this.setState({
                                debitNoteWithoutLines: e.target.checked,
                                debitNoteWithReturnedQyt: false,
                                data: [
                                  {
                                    key: uuidv4(),
                                    asset1: '',
                                    product_sku_name: '',
                                    quantity: 1,
                                    unitPrice: 1,
                                    lot: 0,
                                    taxId: 0,
                                    child_taxes: [{
                                      tax_amount: 0,
                                      tax_type_name: '',
                                    }],
                                    lineCustomFields: [],
                                    lineDiscountType: 'Percent',
                                    discount: 0,
                                    uom_id: null,
                                    uom_info: null,
                                  },
                                ],
                              });
                            }}
                          />
                          <span
                            style={{
                              fontWeight: '500',
                              fontSize: '12px',
                              marginLeft: '5px',
                              color: `${selectedGrn ? '#b0b0b0' : ''}`,
                            }}
                          >
                            Document Contains No Items
                          </span>

                        </div>
                        <div className="form__input-row">
                          <div style={{ marginBottom: '10px' }}>
                            <Checkbox
                              disabled={createDNLoading || updateDNLoading}
                              checked={checkedRecipients}
                              onChange={() => {
                                this.setState(
                                  {
                                    checkedRecipients: !checkedRecipients,
                                  },
                                );
                              }}
                            />
                            <span
                              style={{
                                fontWeight: '500',
                                fontSize: '12px',
                                marginLeft: '5px',
                              }}
                            >
                              Send Email Notification to Recipients
                            </span>
                          </div>
                          {checkedRecipients && (
                            <div className="form__input-row">
                              <PRZSelect
                                className={`form__input-row__input ${(formSubmitted && checkedRecipients && !toRecipients?.length) ? 'form__input-row__input-error' : ''}`}
                                mode="tags"
                                value={toRecipients}
                                filterOption={false}
                                maxTagCount="responsive"
                                onChange={(value) => {
                                  const recipients = [];
                                  for (let i = 0; i < value?.length; i++) {
                                    if (Helpers.validateEmail(value[i])) {
                                      recipients.push(value[i]);
                                    }
                                  }
                                  this.setState({ toRecipients: recipients });
                                }}
                                disabled={createDNLoading || updateDNLoading}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form__lines-wrapper">
                <div className={isQuickView ? 'create-dn__lines-wrapper-drawer' : 'create-dn__lines-wrapper'}>
                  <DNLines
                    title={() => (
                      <div className="create-dn-title__wrapper">
                        <H3Text text={`Debit Note Lines (${data.filter((item) => item.product_sku_name?.length > 0)?.length})`} className="create-po__input-row__label" />
                        <div className="create-dn-title-left">
                          <Checkbox
                            checked={isLineWiseDiscount}
                            onChange={() => {
                              const copyData = JSON.parse(JSON.stringify(data));
                              copyData.map((i) => {
                                i.discount = 0;
                                i.lineDiscountType = 'Percent';
                                i.child_taxes = Helpers.computeTaxation(i?.quantity * i?.unitPrice, i?.taxInfo, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes;
                              });
                              this.setState({
                                data: copyData,
                                isLineWiseDiscount: !isLineWiseDiscount,
                                discountPercentage: 0,
                                discountType: 'Percent',
                              });
                            }}
                            disabled={createDNLoading || updateDNStatusLoading || updateDNLoading}
                          />
                          <div style={{ marginLeft: '5px' }}>Debit Note Level Discount</div>
                        </div>
                      </div>
                    )}
                    isQuickView
                    handleDelete={this.handleDelete}
                    handleProductChange={this.handleProductChange}
                    handleProductChangeValue={this.handleProductChangeValue}
                    data={this.getDataSource(data)}
                    addNewRow={() => this.addNewRow()}
                    updateData={(updatedData) => this.setState({ data: updatedData }, () => {
                      if (isLineWiseDiscount) this.handleDiscountPercentageChange(discountPercentage)
                    })}
                    formSubmitted={formSubmitted}
                    loading={getDNByIdLoading || createDNLoading || updateDNLoading}
                    selectedGrn={!!selectedGrn}
                    toggleBatch={(batch, adjustmentRow) => this.toggleBatch(batch, adjustmentRow)}
                    selectedGrnForDN={selectedGrnForDN}
                    allStock={allStock}
                    toggleAllStock={() => this.setState({ allStock: !allStock })}
                    isLineWiseDiscount={isLineWiseDiscount}
                    selectedCurrencyName={selectedCurrencyName}
                    billFromState={billingAddress?.state}
                    billToState={shippingAddress?.state}
                    getBatches={(batches) => this.getBatches(batches)}
                    debitNoteWithoutLines={debitNoteWithoutLines}
                    debitNoteWithReturnedQyt={debitNoteWithReturnedQyt}
                    tenantDepartmentId={tenantDepartmentId}
                    isAdhocDnLines={isAdhocDnLines}
                    dnFormFromAPInvoice
                    isVendorOverseas={isVendorOverseas}
                  />

                </div>
              </div>

              <div className="form__data-wrapper">
                <div className="ant-row">
                  {!selectedGrn && tenantDepartmentId && !debitNoteWithoutLines && !selectedDN?.data?.debit_notes?.length && selectedSeller && (
                    <div className="ant-col-md-24">
                      <div className="new-row-button" onClick={() => this.addNewRow()}>
                        <span className="new-row-button__icon"><PlusCircleFilled /></span>
                        <div>New Item</div>
                      </div>
                    </div>
                  )}
                  <div className="ant-col-md-12">
                    <div className="form__data-tc">
                      <label className="orgFormLabel">
                        Terms and Conditions
                      </label>
                      <RichTextEditor
                        onChange={(value) => this.handleChangeTextArea(value)}
                        disabled={createDNLoading || updateDNStatusLoading || updateDNLoading}
                        value={termsAndConditions}
                      />
                    </div>

                    {!!selectedTenantTallyIntegrationId && (
                      <div className="form__data-tc">
                        <label className="orgFormLabel">
                          Narration
                        </label>
                        <textarea
                          className="orgFormInput"
                          rows="4"
                          onChange={(event) => this.handleChangeNarration(event.target.value)}
                          disabled={createDNLoading || updateDNStatusLoading || updateDNLoading}
                          value={narration}
                          style={{
                            minHeight: '40px',
                            height: '60px',
                          }}
                        />
                      </div>
                    )}
                    <div className="form__data-attachment">
                      <label className="orgFormLabel">
                        Attachment(s)
                      </label>
                      <Upload
                        action={Constants.UPLOAD_FILE}
                        listType="picture-card"
                        fileList={fileList}
                        disabled={getDNByIdLoading}
                        maxCount={3}
                        multiple
                        onChange={(fileListData) => {
                          this.setState({
                            fileList: fileListData?.fileList?.map((item) => ({
                              ...item,
                              url: item?.response?.response?.location || item?.url,
                            })),
                          });
                        }}
                      >
                        {fileList?.length >= 20 ? null : uploadButton}
                      </Upload>
                    </div>
                  </div>
                  <div className="ant-col-md-12">
                    <div className="form-calculator__wrapper">
                      <div className="form-calculator">
                        <div className="form-calculator__field">
                          <H3Text text="Total Amount" className="form-calculator__field-name" />
                          <H3Text text={MONEY((this.getLineTotals().totalAmount), selectedCurrencyName?.currency_code)} className="form-calculator__field-value" hideText={isDataMaskingPolicyEnable && isHideCostPrice} popOverMessage={"You don't have access to view total amount"} />
                        </div>
                        {isLineWiseDiscount && (
                          <div className="form-calculator__field">
                            <H3Text text="Discount" className="form-calculator__field-name" />
                            <div className="form-calculator__field-value" style={{ display: 'flex' }}>
                              <div style={{ width: '112px' }}>
                                <H3FormInput
                                  value={discountPercentage}
                                  type="number"
                                  containerClassName={`${(formSubmitted && Number(discountPercentage) < 0) ? 'form-error__input' : ''}`}
                                  labelClassName="orgFormLabel"
                                  inputClassName="orgFormInput"
                                  onChange={(e) => {
                                    const copyData = JSON.parse(JSON.stringify(data));
                                    copyData.map((item) => {
                                      const totalValue = copyData?.reduce((acc, cur) => acc + (cur.quantity * cur.unitPrice), 0);

                                      const discountValue = discountType === 'Percent' ? parseFloat(e.target.value || 0) : ((item.quantity * item.unitPrice) / parseFloat(totalValue)) * parseFloat(e.target.value || 0);

                                      const taxableValue = discountType === 'Percent'
                                        ? (item?.quantity * item?.unitPrice) * (1 - discountValue / 100)
                                        : Math.max(item.quantity * item?.unitPrice - discountValue, 0);

                                      item.discount = discountValue;
                                      item.child_taxes = Helpers.computeTaxation(taxableValue, item?.taxInfo, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes;
                                      return data;
                                    });
                                    this.setState({ data: copyData, discountPercentage: parseFloat(e.target.value || 0) });
                                  }}
                                />
                              </div>
                              <div className="discount-type">
                                <PRZSelect
                                  value={discountType}
                                  onChange={(value) => {
                                    const copyData = JSON.parse(JSON.stringify(data));
                                    copyData.map((item) => {
                                      const totalValue = copyData?.reduce((acc, cur) => acc + (cur.quantity * cur.unitPrice), 0);

                                      const discountValue = value === 'Percent' ? Number(discountPercentage) : ((item.quantity * item.unitPrice) / parseFloat(totalValue)) * Number(discountPercentage);

                                      const taxableValue = value === 'Percent'
                                        ? (item?.quantity * item?.unitPrice) * (1 - discountValue / 100)
                                        : Math.max(item.quantity * item?.unitPrice - discountValue, 0);

                                      item.lineDiscountType = value;
                                      item.discount = discountValue;
                                      item.child_taxes = Helpers.computeTaxation(taxableValue, item?.taxInfo, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes;
                                      return item;
                                    });
                                    this.setState({
                                      data: copyData,
                                      discountType: value,
                                    });
                                  }}
                                  style={{
                                    border: '1px dashed rgba(68, 130, 218, 0.2)',
                                    borderRadius: '2px',
                                    height: '28px',
                                    width: '50px',
                                    fontSize: '13px',
                                  }}
                                // disabled
                                >
                                  <Option key="Amount" value="Amount">
                                    {`${selectedCurrencyName?.currency_symbol}`}
                                  </Option>
                                  <Option key="Percent" value="Percent">
                                    %
                                  </Option>
                                </PRZSelect>
                              </div>

                            </div>
                          </div>
                        )}
                        {!isLineWiseDiscount && (
                          <div className="form-calculator__field">
                            <H3Text text="Discount" className="form-calculator__field-name" />
                            <H3Text
                              text={MONEY((this.getLineTotals().totalDiscount), selectedCurrencyName?.currency_code)}
                              className="form-calculator__field-value"
                              hideText={isDataMaskingPolicyEnable && isHideCostPrice}
                              popOverMessage={"You don't have access to view discount"}
                            />
                          </div>
                        )}

                        <div className="form-calculator__field">
                          {/* <H3Text text={charge1Name} className="form-calculator__field-name" /> */}
                          <div
                            className="form-calculator__field-name"
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <H3Text
                              text={charge1Name}
                              style={{ marginRight: '10px' }}
                            />
                            {freightTax && (
                              <div style={{
                                color: '#2d7df7',
                                fontWeight: '400',
                                fontSize: '12px',
                              }}
                              >
                                {`tax@${freightTax}%`}
                              </div>
                            )}
                          </div>
                          <div
                            className="form-calculator__field-value"
                            style={{ display: 'flex' }}
                          >
                            <div style={{ width: '112px' }}>
                              <H3FormInput
                                value={charge1Value}
                                type="number"
                                containerClassName={(formSubmitted && Number(charge1Value) < 0) ? 'form-error__input' : ''}
                                labelClassName="orgFormLabel"
                                inputClassName="orgFormInput"
                                onChange={(e) => {
                                  this.setState({
                                    charge1Value: e.target.value,
                                    freightTaxData: {
                                      ...freightTaxData,
                                      child_taxes: Helpers.computeTaxation(e.target.value, freightTaxInfo, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes,
                                    },
                                  });
                                }}
                              />
                            </div>
                            {!isVendorOverseas && (<FreightTaxInput
                              freightTaxId={freightTaxId}
                              openFreightTax={openFreightTax}
                              sacCode={freightSacCode}
                              setOpenFreightTax={(value) => this.setState({ openFreightTax: value })}
                              setFreightTaxData={(value) => {
                                this.setState({
                                  freightTaxId: !value ? 'Not Applicable' : value?.tax_id,
                                  freightTax: !value ? null : value?.tax_value,
                                  freightTaxInfo: !value ? null : value,
                                  freightTaxData: !value ? {
                                    child_taxes: [
                                      {
                                        tax_amount: 0,
                                        tax_type_name: '',
                                      },
                                    ],
                                  } : {
                                    ...value,
                                    child_taxes: Helpers.computeTaxation(charge1Value, value, billingAddress?.state, shippingAddress?.state)?.tax_info?.child_taxes,
                                  },
                                });
                              }}
                              setSacCode={(value) => this.setState({ freightSacCode: value })}
                            />)}
                          </div>
                        </div>

                        {renderCharges(splitChargesData(chargeData)?.chargeWithTaxName)}
                        <div className="form-calculator__field">
                          <H3Text text="Taxable Amount" className="form-calculator__field-name" />
                          <H3Text text={MONEY((this.getLineTotals().totalBase), selectedCurrencyName?.currency_code)} className="form-calculator__field-value" hideText={isDataMaskingPolicyEnable && isHideCostPrice} popOverMessage="You don't have access to view discount" />
                        </div>

                        {!isVendorOverseas && data?.[0]?.child_taxes?.[0]?.tax_type_name && Helpers.groupAndSumByTaxName(FormHelpers.childTaxesData([...data, freightTaxData, ...chargeData?.flatMap((charge) => (charge?.chargesTaxData))]))?.map((tax, i) => (
                          <Fragment key={i}>
                            <div className="form-calculator__field">
                              <H3Text
                                text={tax?.tax_type_name}
                                className="form-calculator__field-name"
                              />
                              <H3Text
                                text={MONEY((tax?.tax_amount || '0'), selectedCurrencyName?.currency_code)}
                                className="form-calculator__field-value"
                                hideText={isDataMaskingPolicyEnable && isHideCostPrice}
                                popOverMessage={`You don't have access to view ${tax?.tax_type_name.toLowerCase()}`}
                              />
                            </div>
                          </Fragment>
                        ))}

                        {user?.tenant_info?.global_config?.settings?.enable_tds_tcs && user?.tenant_info?.country_code === 'IN' && !isVendorOverseas && (
                          <div className="form-calculator__field">
                            <div className="form-calculator__field-name flex-display" style={{ gap: '10px' }}>
                              <H3Text
                                text="TCS"
                              />
                              <SelectTaxType
                                containerClassName="orgInputContainer"
                                selectedTaxType={taxTypeId}
                                disabled={createDNLoading || updateDNStatusLoading || updateDNLoading}
                                onChange={(value) => {
                                  this.setState({
                                    taxTypeId: value?.tax_id,
                                    taxTypeInfo: value,
                                    taxTypeName: value?.tax_type_name,
                                    taxType: value?.tax_type,
                                  });
                                }}
                                taxTypeName="TCS"
                                customStyle={{
                                  backgroundColor: 'white',
                                  width: '150px',
                                }}
                              />
                            </div>
                            <H3Text
                              text={MONEY((this.getLineTotals().totalTcs || '0'), selectedCurrencyName?.currency_code)}
                              className="form-calculator__field-value"
                            />
                          </div>
                        )}

                        {renderCharges(splitChargesData(chargeData)?.chargeWithoutTaxName)}

                        {/* {chargeData?.map((line) => (
                          <div key={line?.chargeKey} className="form-calculator__field">
                            <div
                              className="form-calculator__field-name"
                            // style={{ marginBottom: '5px', marginRight: '42px' }}
                            >
                              {!user?.tenant_info?.purchase_config?.sub_modules?.purchase_order?.settings?.use_custom_charges ? (
                                <SelectExtraCharge
                                  containerClassName="orgInputContainer"
                                  selectedChargeName={line.charge_name}
                                  disabled={createDNLoading || updateDNStatusLoading || updateDNLoading || !debitNote}
                                  onChange={(value) => {
                                    const copyChargeData = JSON.parse(JSON.stringify(chargeData));
                                    copyChargeData.map((item) => {
                                      if (item.chargeKey === line.chargeKey) {
                                        // eslint-disable-next-line no-param-reassign
                                        item.charge_name = value?.ledger_name;
                                      }
                                    });
                                    this.setState({ chargeData: copyChargeData });
                                  }}
                                  customStyle={{
                                    width: '187px',
                                    backgroundColor: 'white',
                                  }}
                                  excludeCharges={chargeData?.map((item) => item?.charge_name)}
                                  entityName="PURCHASE"
                                />
                              ) : (
                                <H3FormInput
                                  value={line?.charge_name}
                                  type="text"
                                  containerClassName={(formSubmitted && Number(line?.charge_name) <= 0) ? 'form-error__input' : ''}
                                  labelClassName="orgFormLabel"
                                  inputClassName="orgFormInput"
                                  onChange={(e) => {
                                    const copyChargeData = JSON.parse(JSON.stringify(chargeData));
                                    copyChargeData.map((item) => {
                                      if (item.chargeKey === line.chargeKey) {
                                        // eslint-disable-next-line no-param-reassign
                                        item.charge_name = e.target.value;
                                      }
                                      return data;
                                    });
                                    this.setState({ chargeData: copyChargeData });
                                  }}
                                />
                              )}

                            </div>
                            <div
                              className="form-calculator__field-value flex-align-c"
                              style={{ gap: '5px' }}
                            >
                              <H3FormInput
                                value={line?.charge_amount}
                                type="number"
                                containerClassName={(formSubmitted && Number(line?.charge_amount) <= 0) ? 'form-error__input' : ''}
                                labelClassName="orgFormLabel"
                                inputClassName="orgFormInput"
                                onChange={(e) => {
                                  const copyChargeData = JSON.parse(JSON.stringify(chargeData));
                                  copyChargeData.map((item) => {
                                    if (item.chargeKey === line.chargeKey) {
                                      // eslint-disable-next-line no-param-reassign
                                      item.charge_amount = parseFloat(e.target.value);
                                    }
                                    return data;
                                  });
                                  this.setState({ chargeData: copyChargeData });
                                }}
                                style={{ width: '183px' }}
                              />
                              <div>
                                <div className="quote-inputs-conf__delete-line-button" onClick={() => this.handleDeleteCharge(line?.chargeKey)}>
                                  <FontAwesomeIcon icon={faCircleXmark} size="sm" style={{ color: '#6f7276' }} />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))} */}
                        <div className="new-charge-row-button" onClick={() => this.addNewChargesRow()}>
                          <span className="new-charge-row-button__icon"><PlusCircleFilled /></span>
                          <div>Add Charges</div>
                        </div>
                        {user?.tenant_info?.purchase_config?.sub_modules?.debit_note?.settings?.round_off_method !== 'NO_ROUND_OFF' && (
                          <div className="form-calculator__field">
                            <H3Text
                              text="Round Off"
                              className="form-calculator__field-name"
                            />
                            <Tooltip
                              title={`Round Off method for Debit Note is set to ${user?.tenant_info?.purchase_config?.sub_modules?.debit_note?.settings?.round_off_method?.replace(/_/g, ' ')?.toProperCase()}`}
                            >
                              <div style={{ cursor: 'pointer', }}>
                                <FontAwesomeIcon icon={faCircleInfo} size='lg' style={{ color: '#2D7DF7', }} />
                              </div>
                            </Tooltip>
                            <H3Text
                              text={`${Helpers.configuredRoundOff(this.getLineTotals()?.dnTotal, user?.tenant_info?.purchase_config?.sub_modules?.debit_note?.settings?.round_off_method)?.roundOff < 0 ? '(-) ' : ''}${MONEY(
                                Math.abs(Helpers.configuredRoundOff(this.getLineTotals()?.dnTotal, user?.tenant_info?.purchase_config?.sub_modules?.debit_note?.settings?.round_off_method)?.roundOff),
                                selectedCurrencyName?.currency_code
                              )}`}
                              className="form-calculator__field-value"
                              hideText={
                                isDataMaskingPolicyEnable && isHideCostPrice
                              }
                              popOverMessage={
                                "You don't have access to view sub total"
                              }
                            />
                          </div>
                        )}
                        <div className="form-calculator__field form-calculator__field-total">
                          <H3Text text="Grand Total" className="form-calculator__field-name" />
                          <H3Text text={MONEY(Helpers.configuredRoundOff(this.getLineTotals()?.dnTotal, user?.tenant_info?.purchase_config?.sub_modules?.debit_note?.settings?.round_off_method)?.value, selectedCurrencyName?.currency_code)} className="form-calculator__field-value" hideText={isDataMaskingPolicyEnable && isHideCostPrice} popOverMessage={"You don't have access to view grand total"} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        <div className="form__footer">
          <H3Button
            id="save-as-draft"
            text="Save as Draft"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="form__footer-draft"
            onClick={() => {
              if (!createDNLoading) {
                this.createDN(false);
              }
            }}
            isLoading={actionType === 'SAVE_AS_DRAFT' && (createDNLoading || updateDNStatusLoading || updateDNLoading)}
            disabled={createDNLoading || updateDNStatusLoading || updateDNLoading || !debitNote}

          />
          <H3Button
            id="save-and-issue"
            text="Save and Issue"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="form__footer-submit"
            onClick={() => {
              if (!createDNLoading) {
                this.createDN(true);
              }
            }}
            isLoading={actionType === 'SAVE_AND_ISSUE' && (createDNLoading || updateDNStatusLoading || updateDNLoading)}
            disabled={createDNLoading || updateDNStatusLoading || updateDNLoading || !debitNote}
          />
          &nbsp; &nbsp;
          {selectedGrn && ((selectedGrnForDN?.grn_grand_total || selectedGrnForDN?.grand_total) - (selectedGrnForDN?.total_payment_made + selectedGrnForDN?.debit_note_info?.[0]?.db_total) > 0) && (
            <H3Button
              text="Issue and Apply Credit"
              buttonType={defaultButtonTypes.BLUE_ROUNDED}
              customClass="form__footer-submit"
              onClick={() => {
                if (!createDNLoading) {
                  this.createDN(true, true);
                }
              }}
              isLoading={actionType === 'ISSUE_AND_APPLY_CREDIT' && (createDNLoading || updateDNStatusLoading || updateDNLoading)}
              disabled={createDNLoading || updateDNStatusLoading || updateDNLoading || !debitNote}
            />
          )}

          {isDataMaskingPolicyEnable && (isHideCostPrice || isHideSellingPrice) && <RestrictedAccessMessage message={`You don't have access to view or edit${isHideCostPrice ? ' unit price/cost price' : ''}${isHideCostPrice && isHideSellingPrice ? ' and' : ''} ${isHideSellingPrice ? 'selling price' : ''}`} />}

          <div className="form-barcode__wrapper" style={{ display: 'flex' }}>
            {!debitNote && (
              <Popconfirm
                placement="topRight"
                title="This feature is not accessible within your current plan to use this feature contact us."
                onConfirm={() => window.Intercom('showNewMessage')}
                okText="Contact Us"
                cancelText="Cancel"
              >
                <img
                  className="barcode-restrict"
                  src={Crown}
                  alt="premium"
                  style={{
                    marginLeft: '8px',
                    marginTop: '5px',
                  }}
                />
              </Popconfirm>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/**
 *
 * @param UserReducers
 */
const mapStateToProps = ({
  UserReducers, DNReducers, GRNReducers, PurchaseOrderReducers, CFV2Reducers, CurrenciesReducers, TaxReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  grnData: GRNReducers.grnData,
  getGRNLoading: GRNReducers.getGRNLoading,
  createDNLoading: DNReducers.createDNLoading,
  updateDNLoading: DNReducers.updateDNLoading,
  getDNByIdLoading: DNReducers.getDNByIdLoading,
  selectedDN: DNReducers.selectedDN,
  cfV2DocDebitNote: CFV2Reducers.cfV2DocDebitNote,
  CurrenciesResults: CurrenciesReducers.CurrenciesResults,
  getCurrenciesLoading: CurrenciesReducers.getCurrenciesLoading,
  priceMasking: UserReducers.priceMasking,
  taxesGroup: TaxReducers.taxesGroup,
});
/**
 *
 * @param dispatch
 */
const mapDispatchToProps = (dispatch) => ({
  getSellers: (keyword, tenantId, page, limit, sellerId) => dispatch(SellerActions.getSellers(keyword, tenantId, page, limit, sellerId)),
  createDN: (payload, callback) => dispatch(DNActions.createDN(payload, callback)),
  updateDN: (payload, callback) => dispatch(DNActions.updateDN(payload, callback)),
  getDNById: (tenantId, dnId, callback) => dispatch(DNActions.getDNById(tenantId, dnId, callback)),
  getTenantSkuOffer: (tenantProductId, tenantSellerId, callback) => dispatch(OfferActions.getOfferByTenantSku(tenantProductId, tenantSellerId, callback)),
  getDNByIdSuccess: (selectedDN) => dispatch(DNActions.getDNByIdSuccess(selectedDN)),
  updateDNStatus: (payload, callback) => dispatch(DNActions.updateDNStatus(payload, callback)),
  getGRN: (tenantId, grnId, grnEntityId, grnEntityType, status, limit, page, tenantSellerId) => dispatch(GrnActions.getGRN(tenantId, grnId, grnEntityId, grnEntityType, status, limit, page, tenantSellerId)),
  getDocCFV2: (paylaod, callback) => dispatch(CFV2Actions.getDocCFV2(paylaod, callback)),
  getDocCFV2Success: (customFields) => dispatch(CFV2Actions.getDocCFV2Success(customFields)),
  getCurrencies: (orgId) => dispatch(CurrenciesActions.getCurrencies(orgId)),
  getCharges: (orgId, entityName, callback) => dispatch(ExtraChargesActions.getCharges(orgId, entityName, callback)),
  getTaxes: (orgId, page, limit, isActive, isGroup) => dispatch(TaxActions.getTaxes(orgId, page, limit, isActive, isGroup)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DebitNoteForm));
